<template>
	<div class="appointments-week appointments-typography" :class="`is-${revealPhase}`">
		<div class="journal-toolbar">
			<section class="filters-bar" :inert="!currentJournalId">
				<div class="filters-bar-start">
					<AppointmentIcon name="filter" class="filter-bar-icon" />
				</div>

				<!-- Building -->
				<FilterMotion :expanded="editingFilterType === 'building' && !filters.buildingId">
					<div v-if="filters.buildingId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedBuildingName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove building filter" @click.stop="clearFilter('building')">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'building'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="buildingFilterRef"
							:modelValue="filters.buildingId"
							@update:modelValue="(val) => handleFilterValue('buildingId', val)"
							:options="buildingOptions"
							placeholder="Search building..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('building')">
						Building
					</button>
				</FilterMotion>

				<!-- Supplier -->
				<FilterMotion :expanded="editingFilterType === 'supplier' && !filters.supplierId">
					<div v-if="filters.supplierId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedSupplierName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove supplier filter" @click.stop="clearFilter('supplier')">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'supplier'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="supplierFilterRef"
							:modelValue="filters.supplierId"
							@update:modelValue="(val) => handleFilterValue('supplierId', val)"
							:options="supplierOptions"
							placeholder="Search supplier..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('supplier')">
						Supplier
					</button>
				</FilterMotion>

				<!-- Service -->
				<FilterMotion :expanded="editingFilterType === 'service' && !filters.serviceId">
					<div v-if="filters.serviceId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedServiceName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove service filter" @click.stop="clearFilter('service')">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'service'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="serviceFilterRef"
							:modelValue="filters.serviceId"
							@update:modelValue="(val) => handleFilterValue('serviceId', val)"
							:options="serviceOptions"
							placeholder="Search service..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('service')">
						Service
					</button>
				</FilterMotion>

				<!-- Flat ref -->
				<FilterMotion :expanded="editingFilterType === 'flat'">
					<div v-if="filters.flatRef && editingFilterType !== 'flat'" class="filter-chip">
						<span class="filter-chip-text">{{ filters.flatRef }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove flat filter" @click.stop="clearFilter('flat')">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'flat'" class="filter-inline-editor" @click.stop>
						<input
							ref="flatFilterRef"
							type="text"
							v-model="filters.flatRef"
							placeholder="Flat ref..."
							class="filter-inline-text-input"
							@keydown.enter="editingFilterType = null"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('flat')">
						Flat ref
					</button>
				</FilterMotion>
			</section>
			<div v-if="$slots['toolbar-actions']" class="journal-toolbar-actions">
				<slot name="toolbar-actions"></slot>
			</div>
			<div class="journal-tabs-row">
				<JournalViewSwitch :model-value="summaryMode" @update:model-value="$emit('update:summaryMode', $event)" />
				<div class="journal-tabs-actions">
					<slot name="tab-actions"></slot>
				</div>
			</div>
		</div>

		<div v-if="actionError" class="inline-error">
			<p>{{ actionError }}</p>
			<button class="outline-btn" type="button" @click="clearActionError">Dismiss</button>
		</div>

		<div v-if="error" class="state state-error">
			<p>{{ error }}</p>
			<button class="outline-btn" @click="loadWeek">Try again</button>
		</div>
		<div v-else class="week-content-shell">
		<!-- The first-load loader floats over where the week will be, so it can fade out while the week fades in. -->
		<Transition name="week-loader">
			<div v-if="showInitialLoading" class="week-initial-loading" role="status" aria-live="polite">
				<span class="week-loading-spinner" aria-hidden="true"></span>
				<span>Loading appointments…</span>
			</div>
		</Transition>
		<template v-if="!showInitialLoading">
		<div v-if="summaryMode" class="week-rows summary-mode">
			<div class="day-row summary-day">
				<template v-if="summaryRows.length">
					<div class="summary-section">
						<div class="day-header">
							<div class="day-label summary-label">
								<span>{{ summaryLabel }}</span>
							</div>
						</div>
						<div class="appointments-container summary-table">
							<div class="summary-header-row">
								<div
									class="dynamic-fields summary-fields"
									:class="{ 'summary-fields-no-supplier': isSupplierFilterActive }"
								>
									<div class="field-header">Building</div>
									<div v-if="!isSupplierFilterActive" class="field-header">Supplier</div>
									<div class="field-header">Service</div>
									<div class="field-header">Flat</div>
									<div class="field-header">Hours</div>
								</div>
							</div>
							<div
								v-for="row in summaryRows"
								:key="row.id"
								class="appointment-row summary-row"
								:class="{ 'is-merged': row.isMerged }"
							>
								<div class="appointment-details">
									<div
										class="dynamic-fields summary-fields"
										:class="{ 'summary-fields-no-supplier': isSupplierFilterActive }"
									>
										<div class="field-input-container field-building" v-tooltip>
											<span class="field-input summary-input">{{ row.buildingLabel }}</span>
										</div>
										<div
											v-if="!isSupplierFilterActive"
											class="field-input-container field-supplier"
											v-tooltip
										>
											<span class="field-input summary-input">{{ row.supplierLabel }}</span>
										</div>
										<div class="field-input-container field-service" v-tooltip>
											<span class="field-input summary-input">{{ row.serviceLabel }}</span>
										</div>
										<div class="field-input-container field-flat" v-tooltip>
											<span class="field-input summary-input">{{ row.flatLabel }}</span>
										</div>
										<div class="field-input-container field-hours" v-tooltip>
											<span class="field-input summary-input">{{ row.hoursLabel }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="summary-section" :class="{ 'summary-section-with-invoice': invoiceForSummary }">
						<div class="day-header">
							<div class="day-label summary-label">
								<span>Total by service.</span>
							</div>
						</div>
						<div v-if="serviceSummaryRows.length" class="appointments-container summary-table">
							<div class="summary-header-row">
								<div class="dynamic-fields summary-fields-service">
									<div class="field-header">Service</div>
									<div class="field-header">Rate</div>
									<div class="field-header">Hours</div>
									<div class="field-header">Total</div>
								</div>
							</div>
							<div
								v-for="row in serviceSummaryRows"
								:key="row.id"
								class="appointment-row summary-row"
							>
								<div class="appointment-details">
									<div class="dynamic-fields summary-fields-service">
										<div class="field-input-container field-service" v-tooltip>
											<span class="field-input summary-input">{{ row.serviceLabel }}</span><small v-if="row.errorLabel" class="pay-summary-error">{{ row.errorLabel }}</small>
										</div>
										<div class="field-input-container field-rate" v-tooltip>
											<span class="field-input summary-input">{{ row.rateLabel }}</span>
										</div>
										<div class="field-input-container field-hours" v-tooltip>
											<span class="field-input summary-input">{{ row.hoursLabel }}</span>
										</div>
										<div class="field-input-container field-total" v-tooltip>
											<span class="field-input summary-input">{{ row.totalLabel }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else class="state">No services to summarize.</div>

							<div v-if="serviceSummaryTotals && !invoiceForSummary" class="summary-totals-container">
								<div class="summary-totals-spacer">
									<button
										v-if="canShowInvoiceAction"
										class="noc-back-btn"
										type="button"
										:disabled="isInvoiceMatchLoading || isInvoicePreviewOpening"
										@click="handleShowInvoice"
									>
										{{ invoiceActionLabel }}
									</button>
								</div>
							<div class="summary-totals-content">
								<div class="summary-totals-row summary-totals-final">
									<div class="summary-totals-cell">
										<span class="summary-totals-amount">{{ serviceSummaryTotals.hoursLabel }}</span>
									</div>
									<div class="summary-totals-cell">
										<span class="summary-totals-amount">{{ serviceSummaryTotals.totalLabel }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="showCompletedSummary" class="summary-section">
						<div class="day-header">
							<div class="day-label summary-label">Completed appointments: where the timer started and stopped.</div>
						</div>
						<div v-if="completedSummaryRows.length" class="appointments-container summary-table completed-summary-table">
							<div class="summary-header-row">
								<div class="dynamic-fields summary-fields-completed">
									<div class="field-header">Date</div>
									<div class="field-header">Building</div>
									<div class="field-header">Supplier</div>
									<div class="field-header">Service</div>
									<div class="field-header">Flat</div>
									<div class="field-header">Planned</div>
									<div class="field-header">Tracked</div>
									<div class="field-header">Start and stop</div>
								</div>
							</div>
							<div
								v-for="row in completedSummaryRows"
								:key="row.id"
								class="appointment-row summary-row completed-summary-row"
							>
								<div class="appointment-details">
									<div class="dynamic-fields summary-fields-completed">
										<div class="field-input-container field-date" v-tooltip>
											<span class="field-input summary-input">{{ row.dateLabel }}</span>
										</div>
										<div class="field-input-container field-building" v-tooltip>
											<span class="field-input summary-input">{{ row.buildingLabel }}</span>
										</div>
										<div class="field-input-container field-supplier" v-tooltip>
											<span class="field-input summary-input">{{ row.supplierLabel }}</span>
										</div>
										<div class="field-input-container field-service" v-tooltip>
											<span class="field-input summary-input">{{ row.serviceLabel }}</span>
										</div>
										<div class="field-input-container field-flat" v-tooltip>
											<span class="field-input summary-input">{{ row.flatLabel }}</span>
										</div>
										<div class="field-input-container field-hours" v-tooltip>
											<span class="field-input summary-input">{{ row.plannedHoursLabel }}</span>
										</div>
										<div class="field-input-container field-hours" v-tooltip>
											<span class="field-input summary-input">{{ row.trackedHoursLabel }}</span>
										</div>
										<div class="field-input-container summary-history-cell-container">
											<div class="summary-multiline-cell">
												<div
													v-for="entry in row.locationHistory"
													:key="entry.id"
													class="summary-multiline-item"
													:class="`is-${entry.status}`"
												>
													<span>{{ entry.label }}</span>
													<template v-if="entry.mapUrl">
														<span class="summary-location-separator">·</span>
														<a
															class="summary-location-link"
															:href="entry.mapUrl"
															target="_blank"
															rel="noopener noreferrer"
														>Map</a>
													</template>
												</div>
											</div>
											<button
												type="button"
												class="summary-edit-times"
												title="Change start and stop"
												:aria-label="`Change start and stop for ${row.supplierLabel}, ${row.dateLabel}`"
												@click="openTimeDialog(row.timeJob)"
											>
												<AppointmentIcon name="edit" :size="16" />
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else class="state">No completed appointments for this range.</div>
					</div>
				</template>
				<div v-else class="state">No appointments to summarize.</div>
				<SupplierInvoicePurchaseSummary
					v-if="invoiceForSummary"
					:invoice="invoiceForSummary"
					:appointments-total="invoiceAppointmentsTotal"
					:appointments-hours="serviceSummaryTotals?.hoursLabel || ''"
				>
					<template #invoice-action>
						<button
							v-if="canShowInvoiceAction"
							class="noc-back-btn summary-invoice-action"
							type="button"
							:disabled="isInvoiceMatchLoading || isInvoicePreviewOpening"
							@click="handleShowInvoice"
						>
							{{ invoiceActionLabel }}
						</button>
					</template>
				</SupplierInvoicePurchaseSummary>
				<p v-else-if="pinnedSourceInvoiceLookupKey && isInvoiceMatchLoading" class="state" role="status">Loading invoice summary…</p>
				<p v-else-if="pinnedSourceInvoiceLookupKey && !pinnedSupplierInvoice" class="state" role="alert">Could not load the supplier invoice. Reopen its summary from Payments.</p>
			</div>
		</div>
		<div v-else class="week-rows journal-days">
			<div v-for="(day, dayIndex) in weekDates" :key="day.toISOString()" class="day-row" :style="{ '--row-index': dayIndex }">
				<div class="day-card">
					<div class="day-header">
						<div class="day-label">{{ formatDayLabel(day) }}</div>
						<div class="day-actions">
							<div class="action-wrapper">
								<button
									@click.stop="toggleDaySubmenu(day)"
									title="Day options" aria-label="Day options"
									class="more-options-btn"
									:disabled="!appointmentsForDate(day).length || isDeletingDay(day)"
								>
									<AppointmentIcon name="more" class="icon-more" />
								</button>
								<div
									v-if="openDaySubmenuKey === formatISODate(day)"
									class="submenu"
									v-floating-menu
								>
									<HoldToConfirm
										class="submenu-btn delete"
										:disabled="!appointmentsForDate(day).length || isDeletingDay(day)"
										@confirm="handleDeleteDay(day)"
										@cancel="openDaySubmenuKey = null"
									>
										Hold to delete all app. for {{ formatDisplay(day, true) }}
									</HoldToConfirm>
								</div>
							</div>
						</div>
					</div>
					<div class="appointments-container" v-if="appointmentsForDate(day).length">
						<div
							v-for="appointment in appointmentsForDate(day)"
							:key="rowKey(appointment)"
							v-row-enter="newRowKeys.has(rowKey(appointment))"
							class="appointment-row"
							:class="{ 'is-pending': appointment.__pending, 'submenu-open': openSubmenuId === appointment.id }"
						>
							<div class="appointment-details">
								<div class="dynamic-fields">
									<div class="field-input-container field-building">
										<CatalogSearchInput use-fixed
											:modelValue="editingValues[appointment.id].buildingId"
											@update:modelValue="(val) => updateSelect(appointment, 'buildingId', val)"
											:options="buildingOptions"
											:fallback-label="appointment.building?.name || ''"
											placeholder="Building"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-supplier">
										<CatalogSearchInput use-fixed
											:ref="(input) => setCellInput(appointment.id, 'supplierId', input)"
											:modelValue="editingValues[appointment.id].supplierId"
											@update:modelValue="(val) => updateSelect(appointment, 'supplierId', val)"
											:options="supplierOptions"
											:fallback-label="appointment.supplier?.name || ''"
											placeholder="Supplier"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-service">
										<CatalogSearchInput use-fixed
											:ref="(input) => setCellInput(appointment.id, 'serviceId', input)"
											:modelValue="editingValues[appointment.id].serviceId"
											@update:modelValue="(val) => updateSelect(appointment, 'serviceId', val)"
											:options="serviceOptions"
											:fallback-label="appointment.service?.name || ''"
											placeholder="Service"
											v-tooltip
										/>
									</div>
									<div class="field-input-container field-flat">
										<input
											:ref="(input) => setCellInput(appointment.id, 'flatRef', input)"
											type="text"
											v-model="editingValues[appointment.id].flatRef"
											@input="requestSave(appointment)"
											@keydown.enter.prevent="focusNextCell(appointment, 'flatRef')"
											class="field-input field-text"
											placeholder="Flat ref"
										/>
									</div>
									<div class="field-input-container field-hours">
										<input
											:ref="(input) => setCellInput(appointment.id, 'hours', input)"
											type="number" inputmode="decimal"
											min="0"
											step="0.25"
											v-model="editingValues[appointment.id].hours"
											@input="requestHoursSave(appointment)"
											@blur="commitHoursDraft(appointment)"
											@keydown.enter="(event) => { commitHoursOnEnter(event); focusNextCell(appointment, 'hours'); }"
											class="field-input field-number"
											placeholder="Hours"
										/>
									</div>
									<div class="field-input-container field-status">
										<select
											:ref="(input) => setCellInput(appointment.id, 'status', input)"
											v-model="editingValues[appointment.id].status"
											@change="requestSave(appointment)"
											class="field-input field-select"
										>
											<option
												v-for="option in statusOptions"
												:key="option.value"
												:value="option.value"
											>
												{{ option.label }}
											</option>
										</select>
									</div>
								</div>
							</div>
							<div class="appointment-actions">
								<div class="action-wrapper">
									<button
										@click.stop="toggleSubmenu(appointment.id)"
										title="More options" aria-label="More options"
										class="more-options-btn"
									>
										<AppointmentIcon name="more" class="icon-more" />
									</button>
									<div
										v-if="openSubmenuId === appointment.id"
										class="submenu"
										v-floating-menu
									>
										<HoldToConfirm
											class="submenu-btn delete"
											:disabled="isDeleting(appointment.id)"
											@confirm="handleDelete(appointment)"
											@cancel="openSubmenuId = null"
										>
											Hold to delete appointment
										</HoldToConfirm>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="day-footer">
						<button class="noc-back-btn secondary" @click="handleAdd(day)" :disabled="isAdding(day)">
							<AppointmentIcon name="plus" class="btn-icon" /> Add appointment
						</button>
					</div>
				</div>
			</div>
		</div>
		</template>
		<div
			v-if="showWeekLoadingOverlay"
			class="week-loading-overlay"
			role="status"
			aria-live="polite"
		>
			<div class="week-loading-indicator">
				<span class="week-loading-spinner" aria-hidden="true"></span>
				<span>Loading appointments…</span>
			</div>
		</div>
		</div>
			<InvoicePreview
				:show-preview="showInvoicePreview"
				:pdf-url="invoicePreviewPdfUrl"
				@close-preview="closeInvoicePreview"
				@download-pdf="downloadInvoicePreviewPdf"
			/>
			<div
				v-if="showInvoiceChooser"
				class="invoice-chooser-overlay"
				role="dialog"
				aria-modal="true"
				@click="closeInvoiceChooser"
			>
				<div class="invoice-chooser-modal" @click.stop>
					<header class="invoice-chooser-header">
						<div>
							<h3>Choose invoice</h3>
							<p>Multiple invoices match this supplier and exact period.</p>
						</div>
						<button
							type="button"
							class="invoice-chooser-close"
							aria-label="Close invoice chooser"
							@click="closeInvoiceChooser"
						>
							<AppointmentIcon name="close" />
						</button>
					</header>
					<div class="invoice-chooser-list">
						<button
							v-for="invoice in invoiceMatchCandidates"
							:key="getInvoiceSelectionKey(invoice)"
							type="button"
							class="invoice-chooser-item"
							@click="selectInvoiceCandidate(invoice)"
						>
							<span class="invoice-chooser-line">
								<strong>#{{ invoice.invoiceNumber || invoice.documentId || invoice.id }}</strong>
								<span>{{ formatInvoiceChooserDate(invoice.invoiceDate) }}</span>
							</span>
							<span class="invoice-chooser-line">
								<span>{{ formatInvoiceChooserPeriod(invoice) }}</span>
								<span>{{ formatInvoiceChooserAmount(invoice.totalAmount) }}</span>
							</span>
						</button>
					</div>
				</div>
			</div>
			<TrackedTimeDialog
				v-if="timeDialog"
				:job="timeDialog.job"
				:saving="timeDialog.saving"
				:error="timeDialog.error"
				@save="saveTimes"
				@close="timeDialog = null"
			/>
		</div>
	</template>

<script setup>
import { buildPaySummary } from '@/utils/supplierPay';
import { describeWorkLocation, extractUkPostcode, lookupPostcodes } from '@/utils/workLocation';
import { OFFICE_CHECK, formatLondonTime, formatSite } from '@/utils/liveMap';
import TrackedTimeDialog from '@/components/journal/TrackedTimeDialog.vue';
import { settleWithin, whenFontsReady } from '@/utils/pageReveal';
import vFloatingMenu from '@/directives/floatingMenu';
import vRowEnter from '@/directives/rowEnter';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref, watch, onBeforeUnmount, onDeactivated } from 'vue';
import { useJournalAppointmentsStore } from '@/stores/journalAppointments';
import { useJournalAppointmentTemplatesStore } from '@/stores/journalAppointmentTemplates';
import { useCatalogStore } from '@/stores/catalog';
import { useSupplierInvoicesStore } from '@/stores/supplierInvoices';
import { useMediaStore } from '@/stores/media';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import HoldToConfirm from '@/components/HoldToConfirm.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import SupplierInvoicePurchaseSummary from './SupplierInvoicePurchaseSummary.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import FilterMotion from './FilterMotion.vue';
import JournalViewSwitch from './JournalViewSwitch.vue';

// Completed jobs with their timer runs and where the cleaner started and stopped.
const showCompletedSummary = true;

const props = defineProps({
	journal: {
		type: Object,
		default: null,
	},
	summaryMode: {
		type: Boolean,
		default: false,
	},
	initialWeekStart: {
		type: [String, Date],
		default: null,
	},
	initialDateFrom: {
		type: String,
		default: null,
	},
	initialDateTo: {
		type: String,
		default: null,
	},
	initialSupplierId: {
		type: [String, Number],
		default: '',
	},
	sourceSupplierInvoiceId: {
		type: [String, Number],
		default: null,
	},
	sourceSupplierInvoiceDocumentId: {
		type: String,
		default: null,
	},
	handoffKey: {
		type: [String, Number],
		default: null,
	},
});
const emit = defineEmits(['range-change', 'week-loaded', 'reveal', 'update:summaryMode']);

const store = useJournalAppointmentsStore();
const templatesStore = useJournalAppointmentTemplatesStore();
const catalogStore = useCatalogStore();
const supplierInvoicesStore = useSupplierInvoicesStore();
const mediaStore = useMediaStore();
const instance = getCurrentInstance();
const logError = instance?.proxy?.$logError;
const toast = instance?.proxy?.$toast;
const currentJournalId = computed(() => props.journal?.id || null);
const weekStartDate = ref(getWeekStartDate(new Date()));
const editingValues = ref({});
const deletingState = ref({});
const addingState = ref({});
const savingState = reactive({});
const saveTimers = reactive({});
const queuedSaves = reactive({});
const openSubmenuId = ref(null);
const openDaySubmenuKey = ref(null);
const deletingDayState = ref({});
const filters = reactive({
	buildingId: '',
	supplierId: '',
	serviceId: '',
	flatRef: '',
});
const statusOptions = [
	{ value: 'assigned', label: 'Assigned' },
	{ value: 'not_assigned', label: 'Not assigned' },
	{ value: 'in_progress', label: 'In progress' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'cancelled', label: 'Cancelled' },
];

/* ── Filter bar ── */
const editingFilterType = ref(null);
const buildingFilterRef = ref(null);
const supplierFilterRef = ref(null);
const serviceFilterRef = ref(null);
const flatFilterRef = ref(null);

const resolvedBuildingName = computed(() => {
	if (!filters.buildingId) return '';
	const opt = buildingOptions.value.find((o) => String(o.id) === String(filters.buildingId));
	return opt?.name || opt?.label || String(filters.buildingId);
});

const resolvedSupplierName = computed(() => {
	if (!filters.supplierId) return '';
	const opt = supplierOptions.value.find((o) => String(o.id) === String(filters.supplierId));
	return opt?.name || opt?.label || String(filters.supplierId);
});

const resolvedServiceName = computed(() => {
	if (!filters.serviceId) return '';
	const opt = serviceOptions.value.find((o) => String(o.id) === String(filters.serviceId));
	return opt?.name || opt?.label || String(filters.serviceId);
});

const filterRefMap = { building: buildingFilterRef, supplier: supplierFilterRef, service: serviceFilterRef, flat: flatFilterRef };

const startFilterEdit = (key) => {
	editingFilterType.value = key;
	nextTick(() => {
		const target = filterRefMap[key]?.value;
		if (!target) return;
		if (typeof target.focusInput === 'function') {
			target.focusInput();
		} else if (typeof target.focus === 'function') {
			target.focus();
		}
	});
};

const clearFilter = (key) => {
	const mapping = { building: 'buildingId', supplier: 'supplierId', service: 'serviceId', flat: 'flatRef' };
	const field = mapping[key];
	if (field) filters[field] = '';
};

const handleFilterValue = (field, value) => {
	filters[field] = value || '';
	if (value) {
		editingFilterType.value = null;
	}
};

const actionError = ref('');
const isRangeMode = ref(false);
const rangeDateFrom = ref('');
const rangeDateTo = ref('');
const isApplyingTemplate = ref(false);
// { job, saving, error } while the office corrects a finished job's start and stop.
const timeDialog = ref(null);
const initialLoadEmitted = ref(false);
const hasLoadedWeek = ref(false);
const loadedSummaryKey = ref(null);
let loadTimer = null;
let loadPromise = null;
let queuedLoad = false;
let queuedLoadDelay = 0;
const weekCache = new Map();
const prefetchInFlight = new Map();
const MAX_WEEK_CACHE = 12;

const buildingOptions = computed(() => store.buildingOptions);
const supplierOptions = computed(() => store.supplierOptions);
const serviceOptions = computed(() => store.serviceOptions);
const isLoading = computed(() => !currentJournalId.value || store.isLoading);
// Covers the moment before the first request starts too, so an empty week never flashes up.
const showInitialLoading = computed(() => !hasLoadedWeek.value && !error.value);
const showWeekLoadingOverlay = computed(() => isLoading.value && hasLoadedWeek.value);
const error = computed(() => store.error);
const appointmentsByDate = computed(() => {
	const grouped = {};
	(store.items || []).forEach((item) => {
		const key = (item.date || '').substring(0, 10);
		if (!key) return;
		if (!grouped[key]) grouped[key] = [];
		grouped[key].push(item);
	});
	return grouped;
});
const normalizedRange = computed(() => {
	if (!isRangeMode.value) return null;
	const startDate = parseDateInput(rangeDateFrom.value);
	const endDate = parseDateInput(rangeDateTo.value);
	if (!startDate && !endDate) return null;
	let start = startDate || endDate;
	let end = endDate || startDate;
	if (!start || !end) return null;
	if (end < start) {
		[start, end] = [end, start];
	}
	return { startDate: start, endDate: end };
});
const activeRange = computed(() => {
	const range = normalizedRange.value;
	if (range) return range;
	const startDate = weekStartDate.value;
	return { startDate, endDate: addDays(startDate, 6) };
});
const isRangeActive = computed(() => Boolean(normalizedRange.value));

const activeDateFrom = computed(() => formatISODate(activeRange.value.startDate));
const activeDateTo = computed(() => formatISODate(activeRange.value.endDate));

const handleDatePickerUpdate = ({ dateFrom, dateTo }) => {
	if (!dateFrom) return;

	const from = parseDateInput(dateFrom);
	const to = dateTo ? parseDateInput(dateTo) : from;
	if (!from || !to) return;

	// Single date → navigate to the week containing it
	if (formatISODate(from) === formatISODate(to)) {
		isRangeMode.value = false;
		rangeDateFrom.value = '';
		rangeDateTo.value = '';
		weekStartDate.value = getWeekStartDate(from);
		persistWeekStart();
		primeWeekView();
		scheduleLoadWeek();
		return;
	}

	// Date range → enter range mode
	isRangeMode.value = true;
	rangeDateFrom.value = dateFrom;
	rangeDateTo.value = dateTo || dateFrom;
};

const weekDates = computed(() =>
	generateDateRange(activeRange.value.startDate, activeRange.value.endDate),
);
const formattedWeekRange = computed(() =>
	formatDateRange(activeRange.value.startDate, activeRange.value.endDate),
);

const pinnedSupplierInvoice = ref(null);
const invoiceMatchCandidates = ref([]);
const selectedInvoiceCandidateKey = ref('');
const isInvoiceMatchLoading = ref(false);
const isInvoicePreviewOpening = ref(false);
const showInvoicePreview = ref(false);
const showInvoiceChooser = ref(false);
const invoicePreviewPdfUrl = ref('');
const invoicePreviewFileName = ref('');
let invoiceMatchRequestId = 0;

const weekStartYmd = computed(() => formatISODate(activeRange.value.startDate));
const weekEndYmd = computed(() => formatISODate(activeRange.value.endDate));
const pinnedSourceInvoiceLookupKey = computed(() => {
	const documentId = props.sourceSupplierInvoiceDocumentId
		? String(props.sourceSupplierInvoiceDocumentId)
		: '';
	if (documentId) return documentId;
	return props.sourceSupplierInvoiceId === null || props.sourceSupplierInvoiceId === undefined
		? ''
		: String(props.sourceSupplierInvoiceId);
});
const shouldCheckInvoiceMatch = computed(() => {
	return Boolean(
		props.summaryMode &&
		!pinnedSourceInvoiceLookupKey.value &&
		filters.supplierId &&
		weekStartYmd.value &&
		weekEndYmd.value,
	);
});

const getInvoiceSelectionKey = (invoice) => {
	if (!invoice) return '';
	const key = invoice.documentId ?? invoice.id;
	return key === null || key === undefined ? '' : String(key);
};

const selectedInvoiceCandidate = computed(() =>
	invoiceMatchCandidates.value.find(
		(invoice) => getInvoiceSelectionKey(invoice) === selectedInvoiceCandidateKey.value,
	) || null,
);
const dynamicMatchedSupplierInvoice = computed(() => {
	if (invoiceMatchCandidates.value.length === 1) {
		return invoiceMatchCandidates.value[0];
	}
	return selectedInvoiceCandidate.value;
});
const matchedSupplierInvoice = computed(
	() => pinnedSupplierInvoice.value || dynamicMatchedSupplierInvoice.value || null,
);
// Expenses belong to this invoice, not to arbitrary journal filters or weeks.
const invoiceForSummary = computed(() => {
	const invoice = pinnedSupplierInvoice.value;
	if (!invoice || isInvoiceMatchLoading.value || !pinnedSourceInvoiceLookupKey.value) return null;
	if (String(invoice.supplier?.id || '') !== String(filters.supplierId || '')) return null;
	const from = invoice.servicePeriodStart || invoice.servicePeriodEnd;
	const to = invoice.servicePeriodEnd || invoice.servicePeriodStart;
	if (from !== weekStartYmd.value || to !== weekEndYmd.value) return null;
	if (filters.buildingId || filters.serviceId || filters.flatRef) return null;
	return invoice;
});
const requiresInvoiceChoice = computed(
	() =>
		!pinnedSupplierInvoice.value &&
		invoiceMatchCandidates.value.length > 1 &&
		!dynamicMatchedSupplierInvoice.value,
);
const canShowInvoiceAction = computed(
	() => Boolean(matchedSupplierInvoice.value || requiresInvoiceChoice.value),
);
const invoiceActionLabel = computed(() =>
	requiresInvoiceChoice.value ? 'Choose invoice' : 'Show invoice',
);

const resolveInvoiceMatchState = async () => {
	const requestId = (invoiceMatchRequestId += 1);
	isInvoiceMatchLoading.value = true;
	pinnedSupplierInvoice.value = null;
	try {
		if (pinnedSourceInvoiceLookupKey.value) {
			const invoice = await supplierInvoicesStore.fetchInvoiceByIdOrDocumentId(
				pinnedSourceInvoiceLookupKey.value,
			);
			if (requestId !== invoiceMatchRequestId) return;
			pinnedSupplierInvoice.value = invoice;
			invoiceMatchCandidates.value = [];
			selectedInvoiceCandidateKey.value = '';
			showInvoiceChooser.value = false;
			return;
		}

		pinnedSupplierInvoice.value = null;
		showInvoiceChooser.value = false;
		selectedInvoiceCandidateKey.value = '';
		if (!shouldCheckInvoiceMatch.value) {
			invoiceMatchCandidates.value = [];
			return;
		}

		const matches = await supplierInvoicesStore.findInvoiceMatchesByExactPeriod({
			supplierId: filters.supplierId,
			startDate: weekStartYmd.value,
			endDate: weekEndYmd.value,
		});
		if (requestId !== invoiceMatchRequestId) return;
		invoiceMatchCandidates.value = matches;
	} finally {
		if (requestId === invoiceMatchRequestId) {
			isInvoiceMatchLoading.value = false;
		}
	}
};

watch(
	() => [
		props.summaryMode,
		filters.supplierId,
		weekStartYmd.value,
		weekEndYmd.value,
		props.sourceSupplierInvoiceId,
		props.sourceSupplierInvoiceDocumentId,
		props.handoffKey,
	],
	() => {
		void resolveInvoiceMatchState();
	},
	{ immediate: true },
);

const EMPTY_LABEL = '—';
const MULTIPLE_LABEL = 'Multiple';
const MIXED_LABEL = 'Mixed';

const resolveServiceLabel = (id) => {
	const entry = catalogStore.getItemById?.(id);
	if (entry?.name) return entry.name;
	return id ? `Service ${id}` : EMPTY_LABEL;
};

const summaryLabel = computed(() => 'Building and services.');

const isSupplierFilterActive = computed(() => Boolean(filters.supplierId));

const trimNumber = (numeric) => {
	const base = Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
	return base.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const formatStatusLabel = (value) => {
	if (!value) return EMPTY_LABEL;
	const match = statusOptions.find((option) => option.value === value);
	if (match) return match.label;
	return String(value).replace(/_/g, ' ');
};

const toNumber = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : null;
};

const formatMergedHours = (total, hasMissing) => {
	const numeric = Number.isFinite(total) ? total : 0;
	if (numeric === 0 && hasMissing) return EMPTY_LABEL;
	const trimmed = trimNumber(numeric);
	const suffix = hasMissing ? '+' : '';
	return `${trimmed}h${suffix}`;
};

const formatRateValue = (value) => {
	const numeric = toNumber(value);
	if (numeric === null) return EMPTY_LABEL;
	return `£${trimNumber(numeric)}`;
};

const formatTotalValue = (value, hasMissing) => {
	const numeric = toNumber(value);
	if (numeric === null) return EMPTY_LABEL;
	if (numeric === 0 && hasMissing) return EMPTY_LABEL;
	const suffix = hasMissing ? '+' : '';
	return `£${trimNumber(numeric)}${suffix}`;
};

const resolveGroupLabel = (values = []) => {
	const normalized = values.map((value) => String(value || '').trim()).filter(Boolean);
	const unique = Array.from(new Set(normalized));
	if (!unique.length) return EMPTY_LABEL;
	if (unique.length === 1) return unique[0];
	return MULTIPLE_LABEL;
};

const resolveStatusLabel = (values = []) => {
	const normalized = values.map((value) => String(value || '').trim()).filter(Boolean);
	const unique = Array.from(new Set(normalized));
	if (!unique.length) return EMPTY_LABEL;
	if (unique.length === 1) return formatStatusLabel(unique[0]);
	return MIXED_LABEL;
};

const normalizeSummaryKeyPart = (id, label, fallback) => {
	if (id !== null && id !== undefined && id !== '') {
		return `id:${id}`;
	}
	const normalizedLabel = String(label || '').trim().toLowerCase();
	if (normalizedLabel) {
		return `label:${normalizedLabel}`;
	}
	return fallback;
};

const buildSummaryGroupKey = (item) => {
	const buildingId = item?.building?.id ?? null;
	const buildingName = item?.building?.name || '';
	const serviceId = item?.service?.id ?? null;
	const serviceName = item?.service?.name || '';
	const flatRef = item?.flatRef || '';
	const buildingKey = normalizeSummaryKeyPart(buildingId, buildingName, 'building:none');
	const serviceKey = normalizeSummaryKeyPart(serviceId, serviceName, 'service:none');
	const flatKey = normalizeSummaryKeyPart(flatRef, flatRef, 'flat:none');
	return `${buildingKey}|${serviceKey}|${flatKey}`;
};

const summaryRows = computed(() => {
	const items = Array.isArray(store.items) ? store.items : [];
	const grouped = new Map();

	items.forEach((item, index) => {
		const key = buildSummaryGroupKey(item);
		const hoursValue = item?.fixedHours ?? item?.hours ?? null;
		const buildingLabel = item?.building?.name || EMPTY_LABEL;
		const supplierLabel = item?.supplier?.name || EMPTY_LABEL;
		const serviceLabel = item?.service?.name || EMPTY_LABEL;
		const flatLabel = item?.flatRef || EMPTY_LABEL;
		const statusLabel = item?.status || '';
		if (!grouped.has(key)) {
			const hoursNumeric = toNumber(hoursValue);
			grouped.set(key, {
				id: `summary-${key}`,
				sortIndex: index,
				buildingLabels: new Set([buildingLabel]),
				supplierLabels: new Set([supplierLabel]),
				serviceLabels: new Set([serviceLabel]),
				flatLabels: new Set([flatLabel]),
				statuses: new Set([statusLabel]),
				totalHours: hoursNumeric ?? 0,
				hasMissingHours: hoursNumeric === null,
				count: 1,
			});
			return;
		}

		const group = grouped.get(key);
		const hoursNumeric = toNumber(hoursValue);
		group.buildingLabels.add(buildingLabel);
		group.supplierLabels.add(supplierLabel);
		group.serviceLabels.add(serviceLabel);
		group.flatLabels.add(flatLabel);
		group.statuses.add(statusLabel);
		group.count += 1;
		if (hoursNumeric === null) {
			group.hasMissingHours = true;
		} else {
			group.totalHours += hoursNumeric;
		}
	});

	return Array.from(grouped.values())
		.map((group) => ({
			id: group.id,
			sortIndex: group.sortIndex,
			buildingLabel: resolveGroupLabel(Array.from(group.buildingLabels)),
			supplierLabel: resolveGroupLabel(Array.from(group.supplierLabels)),
			serviceLabel: resolveGroupLabel(Array.from(group.serviceLabels)),
			flatLabel: resolveGroupLabel(Array.from(group.flatLabels)),
			hoursLabel: formatMergedHours(group.totalHours, group.hasMissingHours),
			statusLabel: resolveStatusLabel(Array.from(group.statuses)),
			isMerged: group.count > 1,
		}))
		.sort((a, b) => a.sortIndex - b.sortIndex);
});

const paySummary = computed(() => buildPaySummary((store.items || []).map((item) => ({
  ...item,
  supplier: { ...catalogStore.getItemById?.(item.supplier?.id), ...item.supplier },
  service: { ...catalogStore.getItemById?.(item.service?.id), ...item.service },
}))));
const serviceSummaryRows = computed(() => paySummary.value.groups.map((group) => ({
  id: group.key,
  errorLabel: group.errors.join(' '),
  serviceLabel: `${group.serviceName || 'Missing service'} · ${group.engagementType === 'employee' ? 'Employee' : group.engagementType === 'self_employed' ? 'Subcontractor' : 'Unknown engagement'}`,
  hoursLabel: formatMergedHours(group.hours, group.errors.some((error) => error.includes('hours'))),
  rateLabel: formatRateValue(group.rate),
  totalLabel: group.amount === null ? EMPTY_LABEL : formatTotalValue(group.amount, false),
})));
const serviceSummaryTotals = computed(() => paySummary.value.groups.length ? ({
  hoursLabel: formatMergedHours(paySummary.value.totalHours, paySummary.value.lines.some((line) => line.errors.some((error) => error.includes('hours')))),
  totalLabel: paySummary.value.totalAmount === null ? EMPTY_LABEL : formatTotalValue(paySummary.value.totalAmount, false),
}) : null);

const formatHoursLabel = (value) => {
	const numeric = toNumber(value);
	if (numeric === null) return EMPTY_LABEL;
	return `${trimNumber(numeric)}h`;
};

const toSafeSeconds = (value) => {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return null;
	return Math.max(0, Math.round(numeric));
};

const resolveDurationSeconds = ({ seconds = null, minutes = null } = {}) => {
	const directSeconds = toSafeSeconds(seconds);
	if (directSeconds !== null) return directSeconds;
	const minutesNumeric = toNumber(minutes);
	if (minutesNumeric === null) return null;
	return Math.max(0, Math.round(minutesNumeric * 60));
};

const buildSortTimestamp = (...values) => {
	for (const value of values) {
		if (!value) continue;
		const timestamp = new Date(value).getTime();
		if (!Number.isNaN(timestamp)) return timestamp;
	}
	return 0;
};

const resolveAppointmentTrackedSeconds = (item) => {
	const direct = resolveDurationSeconds({
		seconds: item?.actualSeconds,
		minutes: item?.actualMinutes,
	});
	if (direct !== null) return direct;

	const entries = Array.isArray(item?.timeEntries) ? item.timeEntries : [];
	if (!entries.length) return null;

	const latest = [...entries].sort(
		(a, b) =>
			buildSortTimestamp(b?.endAt, b?.startAt) - buildSortTimestamp(a?.endAt, a?.startAt),
	)[0];
	return resolveDurationSeconds({
		seconds: latest?.durationSeconds,
		minutes: latest?.durationMinutes,
	});
};

const formatDurationLabel = (seconds) => {
	const safeSeconds = toSafeSeconds(seconds);
	if (safeSeconds === null) return EMPTY_LABEL;
	const totalMinutes = Math.floor(safeSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const remainderSeconds = safeSeconds % 60;
	const parts = [];
	if (hours) parts.push(`${hours}h`);
	if (minutes || hours) parts.push(`${minutes}m`);
	if (!parts.length) parts.push(`${remainderSeconds}s`);
	return parts.join(' ');
};

// The summary covers one week, so the year is left out.
const formatSummaryDateLabel = (value) => {
	if (!value) return EMPTY_LABEL;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return EMPTY_LABEL;
	return date.toLocaleDateString(undefined, {
		day: '2-digit',
		month: 'short',
	});
};

// London time wherever the office is, matching the Live map and the start and stop editor.
const formatSummaryTimeLabel = (value) => formatLondonTime(value) || EMPTY_LABEL;

const formatSummaryDateTimeLabel = (value) => {
	if (!value) return EMPTY_LABEL;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return EMPTY_LABEL;
	return date.toLocaleString(undefined, {
		day: '2-digit',
		month: 'short',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
};

const normalizeLocationPayload = (payload) => {
	if (!payload || typeof payload !== 'object') return null;
	const latitude = Number(payload.latitude ?? payload.lat);
	const longitude = Number(payload.longitude ?? payload.lng);
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
	if (latitude < -90 || latitude > 90) return null;
	if (longitude < -180 || longitude > 180) return null;
	const accuracyRaw = Number(payload.accuracy ?? payload.accuracyMeters);
	const accuracy = Number.isFinite(accuracyRaw) && accuracyRaw >= 0 ? accuracyRaw : null;
	const capturedAtRaw = payload.capturedAt ? new Date(payload.capturedAt) : null;
	const capturedAt =
		capturedAtRaw && !Number.isNaN(capturedAtRaw.getTime()) ? capturedAtRaw.toISOString() : null;
	return { latitude, longitude, accuracy, capturedAt };
};

const buildLocationMapUrl = (location) =>
	location
		? `https://maps.google.com/?q=${encodeURIComponent(
				`${location.latitude},${location.longitude}`,
			)}`
		: '';

// Postcode centre of each completed job's site, used for "On site" or the distance away.
const siteByPostcode = ref({});
const completedSitePostcodes = computed(() => {
	const items = Array.isArray(store.items) ? store.items : [];
	return [
		...new Set(
			items
				.filter((item) => String(item?.status || '') === 'completed')
				.map((item) => extractUkPostcode(item?.building?.address))
				.filter(Boolean),
		),
	].sort();
});
watch(
	completedSitePostcodes,
	async (postcodes) => {
		if (!showCompletedSummary || !postcodes.length) return;
		const missing = postcodes.filter((postcode) => !(postcode in siteByPostcode.value));
		if (!missing.length) return;
		const found = await lookupPostcodes(missing);
		siteByPostcode.value = { ...siteByPostcode.value, ...found };
	},
	{ immediate: true },
);

const completedSummaryRows = computed(() => {
	const items = Array.isArray(store.items) ? store.items : [];
	return items
		.filter((item) => String(item?.status || '') === 'completed')
		.map((item, index) => {
			const timeEntries = Array.isArray(item?.timeEntries)
				? [...item.timeEntries].sort(
						(a, b) =>
							buildSortTimestamp(a?.startAt, a?.endAt) -
							buildSortTimestamp(b?.startAt, b?.endAt),
					)
				: [];
			const trackedSeconds = resolveAppointmentTrackedSeconds(item);
			const site = siteByPostcode.value[extractUkPostcode(item?.building?.address)] || null;

			// Each timer run gives a Start line and, once stopped, a Stop line. A start or stop the
			// office set says so instead of where the phone was.
			const locationHistory = [];
			timeEntries.forEach((entry, entryIndex) => {
				const flags = Array.isArray(entry?.flagCodes) ? entry.flagCodes : [];
				[
					{ key: 'start', prefix: 'Start', payload: entry?.startLocation, at: entry?.startAt, office: flags.includes('office_start') },
					{ key: 'stop', prefix: 'Stop', payload: entry?.stopLocation, at: entry?.endAt, office: flags.includes('office_stop') },
				].forEach(({ key, prefix, payload, at, office }) => {
					if (key === 'stop' && !entry?.endAt) return;
					const location = office ? null : normalizeLocationPayload(payload);
					const { status, label: placeLabel } = office ? OFFICE_CHECK : describeWorkLocation({ location, site });
					locationHistory.push({
						id: `${key}-loc-${entry?.id ?? entryIndex}`,
						status,
						label: `${prefix} ${formatSummaryTimeLabel(at || location?.capturedAt)} · ${placeLabel}`,
						mapUrl: buildLocationMapUrl(location),
					});
				});
			});
			const plannedHours = Number(item?.fixedHours ?? item?.hours);
			if (!locationHistory.length) {
				locationHistory.push({ id: 'none', status: 'missing', label: 'No timer record', mapUrl: '' });
			}


			return {
				id: item?.id ?? item?.documentId ?? `completed-${index}`,
				sortTimestamp: buildSortTimestamp(
					item?.completedAt,
					item?.actualEndAt,
					item?.date,
				),
				dateLabel: formatSummaryDateLabel(item?.date || item?.completedAt),
				buildingLabel: item?.building?.name || EMPTY_LABEL,
				supplierLabel: item?.supplier?.name || EMPTY_LABEL,
				serviceLabel: item?.service?.name || EMPTY_LABEL,
				flatLabel: item?.flatRef || EMPTY_LABEL,
				plannedHoursLabel: item?.trackTime
					? 'Stopwatch'
					: formatHoursLabel(item?.fixedHours ?? item?.hours),
				trackedHoursLabel:
					trackedSeconds === null ? EMPTY_LABEL : formatDurationLabel(trackedSeconds),
				locationHistory,
				// What the start and stop editor needs.
				timeJob: {
					id: item?.documentId ?? item?.id,
					cleaner: item?.supplier?.name || 'No cleaner',
					site: formatSite({ siteName: item?.building?.name || 'Unnamed site', flat: item?.flatRef }),
					day: String(item?.date || '').slice(0, 10),
					startAt: timeEntries[0]?.startAt || item?.actualStartAt || null,
					endAt: timeEntries.at(-1)?.endAt || item?.actualEndAt || null,
					plannedSeconds: !item?.trackTime && plannedHours > 0 ? Math.round(plannedHours * 3600) : null,
					running: false,
				},
			};
		})
		.sort((a, b) => b.sortTimestamp - a.sortTimestamp);
});

watch(
	formattedWeekRange,
	(range) => {
		emit('range-change', range || '');
	},
	{ immediate: true },
);

const clearActionError = () => {
	actionError.value = '';
};

const setActionError = (message) => {
	actionError.value = message || 'Something went wrong. Please try again.';
};

const getErrorMessage = (err, fallback) =>
	err?.response?.data?.error?.message || err?.message || fallback;

const currentFilters = () => ({
	buildingId: filters.buildingId || null,
	supplierId: filters.supplierId || null,
	serviceId: filters.serviceId || null,
	flatRef: (filters.flatRef || '').trim(),
});

const normalizeFilterId = (value) =>
	value === null || value === undefined || value === '' ? '' : String(value);

const normalizeAppointmentDate = (value) => {
	if (!value) return '';
	const text = String(value);
	if (text.length >= 10) {
		return text.slice(0, 10);
	}
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? '' : formatISODate(parsed);
};

const appointmentMatchesView = (appointment, { startDate, endDate, filters: activeFilters } = {}) => {
	if (!appointment) return false;
	const appointmentDate = normalizeAppointmentDate(appointment.date);
	const rangeStart = formatISODate(startDate);
	const rangeEnd = formatISODate(endDate);
	if (!appointmentDate || !rangeStart || !rangeEnd) return false;
	if (appointmentDate < rangeStart || appointmentDate > rangeEnd) {
		return false;
	}

	const buildingFilter = normalizeFilterId(activeFilters?.buildingId);
	if (buildingFilter && normalizeFilterId(appointment.building?.id) !== buildingFilter) {
		return false;
	}

	const supplierFilter = normalizeFilterId(activeFilters?.supplierId);
	if (supplierFilter && normalizeFilterId(appointment.supplier?.id) !== supplierFilter) {
		return false;
	}

	const serviceFilter = normalizeFilterId(activeFilters?.serviceId);
	if (serviceFilter && normalizeFilterId(appointment.service?.id) !== serviceFilter) {
		return false;
	}

	const flatFilter = String(activeFilters?.flatRef || '').trim().toLowerCase();
	if (flatFilter && !String(appointment.flatRef || '').toLowerCase().includes(flatFilter)) {
		return false;
	}

	return true;
};

const buildWeekCacheKey = ({ startDate, endDate, journalId, filters: cacheFilters }) => {
	const normalized = {
		journalId: journalId ?? '',
		startDate: startDate || '',
		endDate: endDate || '',
		buildingId: cacheFilters?.buildingId ?? '',
		supplierId: cacheFilters?.supplierId ?? '',
		serviceId: cacheFilters?.serviceId ?? '',
		flatRef: cacheFilters?.flatRef ?? '',
	};
	return [
		normalized.journalId,
		normalized.startDate,
		normalized.endDate,
		normalized.buildingId,
		normalized.supplierId,
		normalized.serviceId,
		normalized.flatRef,
	].join('|');
};

const cacheWeekItems = (key, items) => {
	if (!key || !Array.isArray(items)) return;
	if (weekCache.has(key)) {
		weekCache.delete(key);
	}
	weekCache.set(key, items);
	if (weekCache.size > MAX_WEEK_CACHE) {
		const oldestKey = weekCache.keys().next().value;
		if (oldestKey) {
			weekCache.delete(oldestKey);
		}
	}
};

const getCachedWeek = (key) => (key ? weekCache.get(key) : null);

const setWeekItems = (items) => {
	const nextItems = Array.isArray(items) ? items : [];
	store.$patch({
		items: nextItems,
		error: null,
		isLoading: false,
		pagination: {
			page: 1,
			pageSize: nextItems.length,
			total: nextItems.length,
			pageCount: 1,
		},
	});
	seedEditingValues(nextItems);
	clearActionError();
};

const getCacheKeyForRange = (startDate, endDate, cacheFilters) => {
	if (!currentJournalId.value) return null;
	const start = formatISODate(startDate);
	const end = formatISODate(endDate);
	if (!start || !end) return null;
	return buildWeekCacheKey({
		startDate: start,
		endDate: end,
		journalId: currentJournalId.value,
		filters: cacheFilters,
	});
};

const invalidateCachedRange = (startDate, endDate, cacheFilters) => {
	const key = getCacheKeyForRange(startDate, endDate, cacheFilters);
	if (!key) return;
	weekCache.delete(key);
	prefetchInFlight.delete(key);
};

const invalidateCurrentRangeCache = () => {
	const filtersSnapshot = currentFilters();
	const { startDate, endDate } = activeRange.value;
	invalidateCachedRange(startDate, endDate, filtersSnapshot);
};

const applyCachedRange = (startDate, endDate, cacheFilters) => {
	const key = getCacheKeyForRange(startDate, endDate, cacheFilters);
	const cached = getCachedWeek(key);
	if (!cached) return false;
	setWeekItems(cached);
	return true;
};

const primeWeekView = () => {
	const filtersSnapshot = currentFilters();
	const { startDate, endDate } = activeRange.value;
	const usedCache = applyCachedRange(startDate, endDate, filtersSnapshot);
	if (!usedCache) {
		store.$patch({ isLoading: true });
	}
	return filtersSnapshot;
};

const invoiceAppointmentsTotal = computed(() => {
	const key = getCacheKeyForRange(activeRange.value.startDate, activeRange.value.endDate, currentFilters());
	return !isLoading.value && key && loadedSummaryKey.value === key ? paySummary.value.totalAmount : null;
});

const cacheCurrentWeekItems = () => {
	const items = Array.isArray(store.items) ? store.items : [];
	const filtersSnapshot = currentFilters();
	const { startDate, endDate } = activeRange.value;
	const key = getCacheKeyForRange(startDate, endDate, filtersSnapshot);
	if (!key) return;
	cacheWeekItems(key, items);
};

const shouldPrefetchWeeks = computed(() => !isRangeActive.value);

const prefetchWeek = async (startDate, cacheFilters) => {
	if (!startDate || !shouldPrefetchWeeks.value) return;
	const endDate = addDays(startDate, 6);
	const key = getCacheKeyForRange(startDate, endDate, cacheFilters);
	if (!key || weekCache.has(key) || prefetchInFlight.has(key)) return;
	const start = formatISODate(startDate);
	const end = formatISODate(endDate);
	if (!start || !end) return;

	let request = null;
	request = Promise.resolve(
		store.queryAppointmentsInRange({
			startDate: start,
			endDate: end,
			journalId: currentJournalId.value,
			filters: cacheFilters,
		}),
	)
		.then((items) => {
			if (prefetchInFlight.get(key) !== request) return;
			cacheWeekItems(key, items);
		})
		.catch((err) => {
			logError?.(err, 'AppointmentsWeekView prefetchWeek');
		})
		.finally(() => {
			if (prefetchInFlight.get(key) === request) {
				prefetchInFlight.delete(key);
			}
		});

	prefetchInFlight.set(key, request);
};

const prefetchAdjacentWeeks = (startDate, cacheFilters) => {
	if (!startDate || !shouldPrefetchWeeks.value) return;
	prefetchWeek(addDays(startDate, -7), cacheFilters);
	prefetchWeek(addDays(startDate, 7), cacheFilters);
};

const emitInitialWeekLoaded = (items) => {
	if (initialLoadEmitted.value) return;
	if (!Array.isArray(items)) return;
	initialLoadEmitted.value = true;
	emit('week-loaded');
};

// On the first load the page stays still until the week has arrived, then settles in from the top.
const REVEAL_MS = 1100;
const revealPhase = ref('waiting');
let revealTimer = null;
watch(
	() => hasLoadedWeek.value || Boolean(error.value),
	(ready) => {
		if (!ready || revealPhase.value !== 'waiting') return;
		revealPhase.value = 'settling';
		settleWithin([whenFontsReady()], 600).then(() => {
			revealPhase.value = 'revealing';
			emit('reveal');
			revealTimer = setTimeout(() => {
				revealPhase.value = 'shown';
			}, REVEAL_MS);
		});
	},
	{ immediate: true },
);

const finalizeWeekLoad = (items, cacheKey) => {
	loadedSummaryKey.value = cacheKey;
	hasLoadedWeek.value = true;
	emitInitialWeekLoaded(items);
	return items;
};

const loadWeek = async () => {
	if (!currentJournalId.value) return;
	const { startDate, endDate } = activeRange.value;
	const start = formatISODate(startDate);
	const end = formatISODate(endDate);
	if (!start || !end) return;
	const filtersSnapshot = currentFilters();
	const cacheKey = getCacheKeyForRange(startDate, endDate, filtersSnapshot);
	const cached = getCachedWeek(cacheKey);
	if (cached) {
		setWeekItems(cached);
		prefetchAdjacentWeeks(weekStartDate.value, filtersSnapshot);
		return finalizeWeekLoad(cached, cacheKey);
	}
	const inflight = cacheKey ? prefetchInFlight.get(cacheKey) : null;
	if (inflight) {
		try {
			await inflight;
			const prefetched = getCachedWeek(cacheKey);
			if (prefetched) {
				setWeekItems(prefetched);
				prefetchAdjacentWeeks(weekStartDate.value, filtersSnapshot);
				return finalizeWeekLoad(prefetched, cacheKey);
			}
		} catch (err) {
			logError?.(err, 'AppointmentsWeekView loadWeek prefetch');
		}
	}
	try {
		const items = await store.fetchAppointmentsInRange({
			startDate: start,
			endDate: end,
			journalId: currentJournalId.value,
			filters: filtersSnapshot,
		});
		if (cacheKey) {
			cacheWeekItems(cacheKey, items);
		}
		setWeekItems(items);
		prefetchAdjacentWeeks(weekStartDate.value, filtersSnapshot);
		return finalizeWeekLoad(items, cacheKey);
	} catch (err) {
		logError(err, 'AppointmentsWeekView loadWeek');
	}
};

const applyTemplateToWeek = async (templateId) => {
	if (!templateId || !currentJournalId.value) {
		toast?.info?.('Select a template to apply.');
		return;
	}
	if (isRangeActive.value) {
		toast?.info?.('Switch to week view before applying a template.');
		return;
	}
	if (isApplyingTemplate.value) {
		return;
	}
	isApplyingTemplate.value = true;
	try {
		const templateItems = await templatesStore.fetchTemplateItems(templateId);
		if (!templateItems.length) {
			toast?.info?.('This template has no appointments.');
			return;
		}
		const creationPromises = templateItems
			.map((item) => {
				const parsedDayIndex = Number(item.dayIndex);
				const dayIndex = Number.isFinite(parsedDayIndex) ? Math.trunc(parsedDayIndex) : null;
				if (dayIndex === null || dayIndex < 0 || dayIndex > 6) return null;
				const date = formatISODate(addDays(weekStartDate.value, dayIndex));
				if (!date) return null;
				return store.createAppointment({
					date,
					journalId: currentJournalId.value,
					buildingId: item.building?.id || null,
					supplierId: item.supplier?.id || null,
					serviceId: item.service?.id || null,
					flatRef: item.flatRef || '',
					hours: item.hours ?? null,
				});
			})
			.filter(Boolean);
		if (!creationPromises.length) {
			toast?.info?.('No valid template items to apply.');
			return;
		}
		await Promise.all(creationPromises);
		invalidateCurrentRangeCache();
		await loadWeek();
		clearActionError();
		toast?.success?.('Template applied to this week.');
	} catch (err) {
		logError(err, 'AppointmentsWeekView applyTemplateToWeek');
		setActionError(getErrorMessage(err, 'Failed to apply template.'));
	} finally {
		isApplyingTemplate.value = false;
	}
};

const scheduleLoadWeek = (delay = 0) => {
	queuedLoadDelay = delay;
	if (loadTimer) {
		clearTimeout(loadTimer);
	}
	loadTimer = setTimeout(() => {
		loadTimer = null;
		if (loadPromise) {
			queuedLoad = true;
			return;
		}
		loadPromise = loadWeek().finally(() => {
			loadPromise = null;
			if (queuedLoad) {
				queuedLoad = false;
				scheduleLoadWeek(queuedLoadDelay);
			}
		});
	}, delay);
};

const seedEditingValues = (items = []) => {
	const map = {};
	items.forEach((item) => {
		map[item.id] = {
			buildingId: item.building?.id || '',
			supplierId: item.supplier?.id || '',
			serviceId: item.service?.id || '',
			flatRef: item.flatRef || '',
			hours: item.hours ?? '',
			status: item.status || 'assigned',
		};
	});
	editingValues.value = map;
};

const ensureEditingEntries = (items = []) => {
	const next = { ...editingValues.value };
	let changed = false;
	items.forEach((item) => {
		if (!item?.id || next[item.id]) return;
		next[item.id] = {
			buildingId: item.building?.id || '',
			supplierId: item.supplier?.id || '',
			serviceId: item.service?.id || '',
			flatRef: item.flatRef || '',
			hours: item.hours ?? '',
			status: item.status || 'assigned',
		};
		changed = true;
	});
	if (changed) {
		editingValues.value = next;
	}
};

const findAppointmentById = (id) => {
	if (!id) return null;
	return store.items.find((item) => item.id === id) || null;
};

const isBlankHoursValue = (value) =>
	value === '' || value === null || value === undefined;

const clearPendingSave = (appointmentId) => {
	if (!appointmentId || !saveTimers[appointmentId]) return;
	clearTimeout(saveTimers[appointmentId]);
	saveTimers[appointmentId] = null;
};

const requestSave = (appointment, { debounce = true } = {}) => {
	const target = findAppointmentById(appointment?.id) || appointment;
	if (!target?.id) {
		return;
	}
	if (debounce) {
		clearPendingSave(target.id);
		saveTimers[target.id] = setTimeout(() => {
			saveTimers[target.id] = null;
			saveAppointment(target);
		}, 350);
	} else {
		clearPendingSave(target.id);
		saveAppointment(target);
	}
};

const requestHoursSave = (appointment) => {
	const target = findAppointmentById(appointment?.id) || appointment;
	const entry = target?.id ? editingValues.value[target.id] : null;
	if (!entry || !target?.id) return;

	if (isBlankHoursValue(entry.hours)) {
		clearPendingSave(target.id);
		return;
	}

	requestSave(target);
};

const commitHoursDraft = (appointment) => {
	const target = findAppointmentById(appointment?.id) || appointment;
	const entry = target?.id ? editingValues.value[target.id] : null;
	if (!entry || !target?.id) return;

	if (isBlankHoursValue(entry.hours)) {
		clearPendingSave(target.id);
		const alreadyStoredAsTrackedTime =
			isBlankHoursValue(target.hours) &&
			isBlankHoursValue(target.fixedHours) &&
			target.trackTime === true;
		if (!alreadyStoredAsTrackedTime) {
			requestSave(target, { debounce: false });
		}
		return;
	}

	if (saveTimers[target.id]) {
		requestSave(target, { debounce: false });
	}
};

const commitHoursOnEnter = (event) => {
	event?.currentTarget?.blur?.();
};

const snapshotEditingValues = (appointmentId) => {
	const current = editingValues.value?.[appointmentId];
	if (!current) return null;
	return { values: { ...current } };
};

const mergeEditedFields = (appointmentId, entry, snapshot) => {
	if (!snapshot?.values) return entry;
	const current = editingValues.value?.[appointmentId];
	if (!current) return entry;
	const merged = { ...entry };
	Object.keys(entry).forEach((field) => {
		if (current[field] !== snapshot.values[field] && current[field] !== undefined) {
			merged[field] = current[field];
		}
	});
	return merged;
};

const syncUpdatedAppointment = (updated, { snapshot } = {}) => {
	if (!updated?.id) {
		return false;
	}
	const items = store.items || [];
	const index = items.findIndex((item) => item.id === updated.id);
	if (index === -1) {
		return false;
	}
	if (
		!appointmentMatchesView(updated, {
			startDate: activeRange.value.startDate,
			endDate: activeRange.value.endDate,
			filters: currentFilters(),
		})
	) {
		removeAppointmentLocally(items[index]);
		return true;
	}
	const next = [...items];
	next[index] = { ...items[index], ...updated };
	store.$patch({ items: next });
	const entry = {
		buildingId: updated.building?.id || '',
		supplierId: updated.supplier?.id || '',
		serviceId: updated.service?.id || '',
		flatRef: updated.flatRef || '',
		hours: updated.hours ?? '',
		status: updated.status || 'assigned',
	};
	const mergedEntry = mergeEditedFields(updated.id, entry, snapshot);
	editingValues.value = {
		...editingValues.value,
		[updated.id]: mergedEntry,
	};
	return true;
};

const removeAppointmentLocally = (appointment) => {
	if (!appointment?.id) {
		return null;
	}
	const items = store.items || [];
	const index = items.findIndex((entry) => entry.id === appointment.id);
	if (index === -1) {
		return null;
	}
	const removedEntry = items[index];
	const next = [...items];
	next.splice(index, 1);
	store.$patch({ items: next });
	const editingSnapshot = editingValues.value[appointment.id]
		? { ...editingValues.value[appointment.id] }
		: null;
	const nextEditing = { ...editingValues.value };
	delete nextEditing[appointment.id];
	editingValues.value = nextEditing;
	return {
		index,
		entry: removedEntry,
		editing: editingSnapshot,
	};
};

const restoreAppointmentSnapshot = (snapshot) => {
	if (!snapshot?.entry || snapshot.index === undefined) {
		return false;
	}
	const items = store.items || [];
	const next = [...items];
	const insertIndex = Math.min(snapshot.index, next.length);
	next.splice(insertIndex, 0, snapshot.entry);
	store.$patch({ items: next });
	if (snapshot.entry?.id && snapshot.editing) {
		editingValues.value = {
			...editingValues.value,
			[snapshot.entry.id]: snapshot.editing,
		};
	}
	return true;
};

// A row created with "Add appointment" keeps its placeholder's key after saving,
// so it isn't re-created while its entrance is still playing.
const rowKeyAliases = new Map();
const rowKey = (appointment) => rowKeyAliases.get(appointment.id) || appointment.id;
const newRowKeys = ref(new Set());
const markRowAsNew = (key) => {
	newRowKeys.value.add(key);
	setTimeout(() => newRowKeys.value.delete(key), 1500);
};

const createPendingAppointment = (
	dayKey,
	{ buildingId = '', supplierId = '', serviceId = '', flatRef = '' } = {},
) => {
	const tempId = `pending-${dayKey}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const placeholder = {
		id: tempId,
		documentId: null,
		date: dayKey,
		__pending: true,
		building: buildingId ? { id: buildingId } : null,
		supplier: supplierId ? { id: supplierId } : null,
		service: serviceId ? { id: serviceId } : null,
		flatRef: flatRef || '',
		hours: null,
		status: 'assigned',
	};
	store.$patch({ items: [...(store.items || []), placeholder] });
	editingValues.value = {
		...editingValues.value,
		[tempId]: {
			buildingId: buildingId || '',
			supplierId: supplierId || '',
			serviceId: serviceId || '',
			flatRef: flatRef || '',
			hours: '',
			status: 'assigned',
		},
	};
	return placeholder;
};

const replacePendingAppointment = (tempId, created) => {
	if (!created?.id) {
		return false;
	}
	const items = store.items || [];
	const index = items.findIndex((entry) => entry.id === tempId);
	if (index === -1) {
		return false;
	}
	rowKeyAliases.set(created.id, tempId);
	const { __pending: _ignored, ...fallbackEntry } = items[index] || {};
	const mergedBuilding = created.building || fallbackEntry.building || null;
	const mergedSupplier = created.supplier || fallbackEntry.supplier || null;
	const mergedService = created.service || fallbackEntry.service || null;
	const mergedFlatRef = created.flatRef ?? fallbackEntry.flatRef ?? '';
	const nextEntries = [...items];
	nextEntries[index] = {
		...fallbackEntry,
		...created,
		__pending: false,
		building: mergedBuilding,
		supplier: mergedSupplier,
		service: mergedService,
		flatRef: mergedFlatRef,
	};
	store.$patch({ items: nextEntries });
	const { [tempId]: _, ...rest } = editingValues.value;
	editingValues.value = {
		...rest,
		[created.id]: {
			buildingId: mergedBuilding?.id || '',
			supplierId: mergedSupplier?.id || '',
			serviceId: mergedService?.id || '',
			flatRef: mergedFlatRef || '',
			hours: created.hours ?? '',
			status: created.status || 'assigned',
		},
	};
	return true;
};

const handleClickOutside = (event) => {
	if (!event.target.closest('.action-wrapper')) {
		openSubmenuId.value = null;
		openDaySubmenuKey.value = null;
	}
	if (editingFilterType.value && !event.target.closest('.filter-inline-editor')) {
		editingFilterType.value = null;
	}
};

onMounted(async () => {
	document.addEventListener('click', handleClickOutside, true);
	try {
		if (!catalogStore.initialized) {
			await catalogStore.bootstrap?.();
		}
	} catch (err) {
		logError(err, 'AppointmentsWeekView bootstrapCatalog');
	}
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside, true);
	clearTimeout(revealTimer);
	if (loadTimer) {
		clearTimeout(loadTimer);
		loadTimer = null;
	}
	loadPromise = null;
	queuedLoad = false;
	Object.values(saveTimers).forEach((timer) => {
		if (timer) {
			clearTimeout(timer);
		}
	});
});

watch(
	currentJournalId,
	async (journalId, previousJournalId) => {
		if (!journalId) return;
		if (journalId) {
			const stored = store.weekStartForJournal(journalId);
			if (stored) {
				weekStartDate.value = getWeekStartDate(new Date(stored));
			} else {
				weekStartDate.value = getWeekStartDate(new Date());
				store.setWeekStartForJournal(journalId, weekStartDate.value.toISOString());
			}
		}
		if (previousJournalId === null) {
			// The shell mounted before journals arrived; preserve the requested range
			// and keep its loader visible until the first appointment request finishes.
			applyInitialInvoiceContext();
			primeWeekView();
		}
		scheduleLoadWeek();
	},
	{ immediate: true },
);

const applyInitialSupplier = (value) => {
	const normalized = value === null || value === undefined ? '' : String(value);
	if (normalized && filters.supplierId !== normalized) {
		filters.supplierId = normalized;
	}
};

const normalizeInitialDateRange = (dateFrom, dateTo) => {
	const startDate = parseDateInput(dateFrom);
	const endDate = parseDateInput(dateTo);
	if (!startDate && !endDate) return null;
	let start = startDate || endDate;
	let end = endDate || startDate;
	if (!start || !end) return null;
	if (end < start) {
		[start, end] = [end, start];
	}
	const normalizedFrom = formatISODate(start);
	const normalizedTo = formatISODate(end);
	if (!normalizedFrom || !normalizedTo) return null;
	return {
		dateFrom: normalizedFrom,
		dateTo: normalizedTo,
		startDate: start,
	};
};

const applyInitialDateRange = (dateFrom, dateTo) => {
	const normalized = normalizeInitialDateRange(dateFrom, dateTo);
	if (!normalized) return false;

	const currentFrom = rangeDateFrom.value || '';
	const currentTo = rangeDateTo.value || '';
	const currentMode = isRangeMode.value;
	const nextWeekStart = getWeekStartDate(normalized.startDate);
	const currentWeekIso = formatISODate(weekStartDate.value);
	const nextWeekIso = formatISODate(nextWeekStart);

	if (
		currentMode &&
		currentFrom === normalized.dateFrom &&
		currentTo === normalized.dateTo &&
		currentWeekIso === nextWeekIso
	) {
		return true;
	}

	isRangeMode.value = true;
	rangeDateFrom.value = normalized.dateFrom;
	rangeDateTo.value = normalized.dateTo;
	weekStartDate.value = nextWeekStart;
	persistWeekStart();
	primeWeekView();
	scheduleLoadWeek();
	return true;
};

const applyInitialInvoiceContext = () => {
	if (pinnedSourceInvoiceLookupKey.value) {
		filters.buildingId = '';
		filters.serviceId = '';
		filters.flatRef = '';
	}
	applyInitialSupplier(props.initialSupplierId);
	if (applyInitialDateRange(props.initialDateFrom, props.initialDateTo)) {
		return;
	}
	applyInitialWeekStart(props.initialWeekStart);
};

const applyInitialWeekStart = (value) => {
	if (!value) return;
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return;
	const nextStart = getWeekStartDate(parsed);
	const currentIso = formatISODate(weekStartDate.value);
	const nextIso = formatISODate(nextStart);
	if (!nextIso || nextIso === currentIso) return;
	weekStartDate.value = nextStart;
	persistWeekStart();
	primeWeekView();
	scheduleLoadWeek();
};

watch(
	() => props.initialSupplierId,
	(next) => {
		if (props.handoffKey !== null && props.handoffKey !== undefined) return;
		applyInitialSupplier(next);
	},
	{ immediate: true },
);

watch(
	() => [props.initialDateFrom, props.initialDateTo],
	([nextFrom, nextTo]) => {
		if (props.handoffKey !== null && props.handoffKey !== undefined) return;
		void applyInitialDateRange(nextFrom, nextTo);
	},
	{ immediate: true },
);

watch(
	() => props.initialWeekStart,
	(next) => {
		if (props.handoffKey !== null && props.handoffKey !== undefined) return;
		if (props.initialDateFrom || props.initialDateTo) return;
		applyInitialWeekStart(next);
	},
	{ immediate: true },
);

watch(
	() => props.handoffKey,
	(next) => {
		if (next === null || next === undefined) return;
		applyInitialInvoiceContext();
	},
	{ immediate: true },
);

watch(
	() => store.items,
	(items) => {
		if (Array.isArray(items)) {
			ensureEditingEntries(items);
		}
	},
	{ deep: false, immediate: true }
);

watch(
	() => [filters.buildingId, filters.supplierId, filters.serviceId, filters.flatRef],
	() => {
		scheduleLoadWeek(400);
	},
);

watch(
	() => [isRangeMode.value, rangeDateFrom.value, rangeDateTo.value],
	() => {
		if (!isRangeMode.value) return;
		if (!normalizedRange.value) return;
		primeWeekView();
		scheduleLoadWeek();
	},
);

const appointmentsForDate = (day) => {
	const key = formatISODate(day);
	return appointmentsByDate.value[key] || [];
};

function persistWeekStart() {
	if (currentJournalId.value) {
		store.setWeekStartForJournal(
			currentJournalId.value,
			weekStartDate.value.toISOString(),
		);
	}
}

const changeWeek = (days) => {
	if (isRangeMode.value) {
		isRangeMode.value = false;
		rangeDateFrom.value = '';
		rangeDateTo.value = '';
	}
	weekStartDate.value = addDays(weekStartDate.value, days);
	persistWeekStart();
	primeWeekView();
	scheduleLoadWeek();
};

const handleAdd = async (day) => {
	const key = formatISODate(day);
	if (!key || !currentJournalId.value) return;
	if (addingState.value[key]) {
		toast?.info?.('Already creating an appointment for this day. Please wait.');
		return;
	}
	addingState.value[key] = true;
	const prefill = {
		buildingId: filters.buildingId || '',
		supplierId: filters.supplierId || '',
		serviceId: filters.serviceId || '',
		flatRef: filters.flatRef || '',
	};
	const placeholder = createPendingAppointment(key, prefill);
	markRowAsNew(placeholder.id);
	try {
		const created = await store.createAppointment({
			date: key,
			journalId: currentJournalId.value,
			buildingId: prefill.buildingId || null,
			supplierId: prefill.supplierId || null,
			serviceId: prefill.serviceId || null,
			flatRef: prefill.flatRef || '',
		});
		const replaced = replacePendingAppointment(placeholder.id, created);
		if (!replaced) {
			await loadWeek();
		}
		clearActionError();
		if (replaced) {
			invalidateCurrentRangeCache();
			cacheCurrentWeekItems();
		}
	} catch (err) {
		removeAppointmentLocally(placeholder);
		logError(err, 'AppointmentsWeekView handleAdd');
		setActionError(getErrorMessage(err, 'Failed to create appointment.'));
	} finally {
		addingState.value[key] = false;
	}
};

const saveAppointment = async (appointment) => {
	const current = findAppointmentById(appointment?.id) || appointment;
	const entry = current?.id ? editingValues.value[current.id] : null;
	if (!entry || !current?.documentId) return;
	if (savingState[current.id]) {
		queuedSaves[current.id] = true;
		return;
	}
	savingState[current.id] = true;
	try {
		const snapshot = snapshotEditingValues(current.id);
		const hoursFieldBlank = isBlankHoursValue(entry.hours);
		const parsedHours = hoursFieldBlank ? null : Number(entry.hours);
		if (!hoursFieldBlank && Number.isNaN(parsedHours)) {
			setActionError('Enter a valid hours value.');
			return;
		}
		const resolvedHours = parsedHours;
		if (resolvedHours !== null && resolvedHours < 0) {
			setActionError('Hours cannot be negative.');
			return;
		}
		const trackTime = resolvedHours === null;
		const fixedHours = trackTime ? null : resolvedHours;
		// Send the status only when the office changed it here. A row loaded before the cleaner
		// started or finished would otherwise reset the job and erase its timer record.
		const nextStatus = entry.status || 'assigned';
		const statusChanged = nextStatus !== (current.status || 'assigned');
		const updated = await store.updateAppointment(current.documentId, {
			journal: currentJournalId.value,
			building: entry.buildingId || null,
			supplier: entry.supplierId || null,
			service: entry.serviceId || null,
			flatRef: entry.flatRef || '',
			hours: resolvedHours,
			trackTime,
			fixedHours,
			...(statusChanged ? { status: nextStatus } : {}),
		}, current);
		invalidateCurrentRangeCache();
		const synced = syncUpdatedAppointment(updated, { snapshot });
		if (!synced) {
			await loadWeek();
		}
		clearActionError();
		cacheCurrentWeekItems();
	} catch (err) {
		logError(err, 'AppointmentsWeekView saveAppointment');
		setActionError(getErrorMessage(err, 'Failed to update appointment.'));
	} finally {
		delete savingState[current.id];
		if (queuedSaves[current.id]) {
			queuedSaves[current.id] = false;
			saveAppointment(findAppointmentById(current.id) || current);
		}
	}
};

const updateSelect = (appointment, field, value) => {
	const entry = editingValues.value[appointment.id];
	if (!entry) return;
	const normalizedValue = value ?? '';
	if (entry[field] === normalizedValue) {
		return;
	}
	entry[field] = normalizedValue;
	requestSave(appointment, { debounce: false });
	if (normalizedValue) {
		focusNextEmptyCell(appointment, field);
	}
};

// Filling a row flows from cell to cell: choosing from a list moves on to the next empty cell
// and opens it; Enter in Flat ref or Hours moves to the next cell.
const CELL_ORDER = ['buildingId', 'supplierId', 'serviceId', 'flatRef', 'hours', 'status'];
const cellInputs = new Map();
const setCellInput = (id, field, input) => {
	const key = `${id}:${field}`;
	if (input) cellInputs.set(key, input);
	else cellInputs.delete(key);
};
const focusCell = (id, field) => nextTick(() => {
	const target = cellInputs.get(`${id}:${field}`);
	const element = target?.$el ? target.$el.querySelector('input') : target;
	if (!element) return;
	element.focus();
	if (field === 'status') {
		try {
			element.showPicker?.();
		} catch {
			// Some browsers only open a select's list from a direct click.
		}
	}
});
const isEmptyCell = (value) => value === '' || value === null || value === undefined;
// Cells that are already filled (e.g. prefilled from the filters) are skipped.
const focusNextEmptyCell = (appointment, field) => {
	const entry = editingValues.value[appointment.id];
	const start = CELL_ORDER.indexOf(field);
	if (!entry || start === -1) return;
	const next = CELL_ORDER.slice(start + 1, CELL_ORDER.indexOf('status')).find((name) => isEmptyCell(entry[name]));
	if (next) focusCell(appointment.id, next);
};
const focusNextCell = (appointment, field) => {
	const next = CELL_ORDER[CELL_ORDER.indexOf(field) + 1];
	if (next) focusCell(appointment.id, next);
};

const toggleSubmenu = (appointmentId) => {
	if (openSubmenuId.value === appointmentId) {
		openSubmenuId.value = null;
	} else {
		openDaySubmenuKey.value = null;
		openSubmenuId.value = appointmentId;
	}
};

const isDeletingDay = (day) => {
	const key = formatISODate(day);
	return Boolean(key && deletingDayState.value[key]);
};

const toggleDaySubmenu = (day) => {
	const key = formatISODate(day);
	if (!key) return;
	if (!appointmentsForDate(day).length || isDeletingDay(day)) return;
	if (openDaySubmenuKey.value === key) {
		openDaySubmenuKey.value = null;
	} else {
		openSubmenuId.value = null;
		openDaySubmenuKey.value = key;
	}
};

const handleDelete = async (appointment) => {
	openSubmenuId.value = null;
	openDaySubmenuKey.value = null;
	const documentId = appointment.documentId;
	const entryId = appointment.id;
	if (!documentId || !entryId) return;
	const snapshot = removeAppointmentLocally(appointment);
	deletingState.value[entryId] = true;
	try {
		await store.deleteAppointment(documentId, appointment);
		if (!snapshot) {
			await loadWeek();
		}
		clearActionError();
		if (snapshot) {
			invalidateCurrentRangeCache();
			cacheCurrentWeekItems();
		}
	} catch (err) {
		const restored = restoreAppointmentSnapshot(snapshot);
		if (!restored) {
			await loadWeek();
		}
		logError(err, 'AppointmentsWeekView handleDelete');
		setActionError(getErrorMessage(err, 'Failed to delete appointment.'));
	} finally {
		deletingState.value[entryId] = false;
	}
};

const handleDeleteDay = async (day) => {
	const key = formatISODate(day);
	if (!key) return;
	const dayAppointments = appointmentsForDate(day);
	if (!dayAppointments.length) {
		openDaySubmenuKey.value = null;
		toast?.info?.('No appointments to delete for this day.');
		return;
	}
	if (deletingDayState.value[key]) return;
	openDaySubmenuKey.value = null;
	deletingDayState.value[key] = true;
	try {
		invalidateCurrentRangeCache();
		const deletions = dayAppointments
			.filter((appointment) => appointment.documentId)
			.map((appointment) => store.deleteAppointment(appointment.documentId, appointment));
		if (deletions.length) {
			await Promise.all(deletions);
		}
		await loadWeek();
		clearActionError();
	} catch (err) {
		logError(err, 'AppointmentsWeekView handleDeleteDay');
		setActionError(getErrorMessage(err, 'Failed to delete appointments.'));
		await loadWeek();
	} finally {
		deletingDayState.value[key] = false;
	}
};

const isDeleting = (id) => !!deletingState.value[id];
const isAdding = (day) => !!addingState.value[formatISODate(day)];

const formatDayLabel = (date) => {
	const weekday = date.toLocaleDateString(undefined, { weekday: 'long' });
	const day = date.getDate();
	const month = date.toLocaleDateString(undefined, { month: 'long' });
	return `${weekday}, ${day} ${month}`;
};

function formatDateRange(startDate, endDate) {
	const start = new Date(startDate);
	const end = new Date(endDate);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return '';
	}

	const startDay = start.getDate();
	const endDay = end.getDate();
	const startMonth = start.toLocaleDateString(undefined, { month: 'long' });
	const endMonth = end.toLocaleDateString(undefined, { month: 'long' });
	const startYear = start.getFullYear();
	const endYear = end.getFullYear();
	const sameYear = startYear === endYear;
	const sameMonth = sameYear && start.getMonth() === end.getMonth();

	if (sameMonth) {
		return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
	}
	if (sameYear) {
		return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
	}
	return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
}

function getWeekStartDate(date) {
	const d = new Date(date);
	const weekday = d.getDay();
	const diff = (weekday + 6) % 7;
	d.setHours(0, 0, 0, 0);
	d.setDate(d.getDate() - diff);
	return d;
}

function addDays(date, days) {
	const d = new Date(date);
	d.setDate(d.getDate() + days);
	return d;
}

function addMonths(date, months) {
	const d = new Date(date);
	const day = d.getDate();
	d.setDate(1);
	d.setMonth(d.getMonth() + months);
	const daysInMonth = getDaysInMonth(d);
	d.setDate(Math.min(day, daysInMonth));
	return d;
}

function startOfMonth(date) {
	const d = new Date(date);
	d.setDate(1);
	d.setHours(0, 0, 0, 0);
	return d;
}

function getDaysInMonth(date) {
	const d = new Date(date);
	return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function getMondayIndex(date) {
	const day = new Date(date).getDay();
	return (day + 6) % 7;
}

function parseDateInput(value) {
	if (!value) return null;
	const parts = String(value).split('-').map((part) => Number(part));
	if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
		return null;
	}
	const [year, month, day] = parts;
	if (!year || !month || !day) return null;
	const date = new Date(year, month - 1, day);
	return Number.isNaN(date.getTime()) ? null : date;
}

function formatMonthYear(date) {
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function generateDateRange(startDate, endDate) {
	const start = new Date(startDate);
	const end = new Date(endDate);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return [];
	}
	if (end < start) {
		return [];
	}
	const dayMs = 24 * 60 * 60 * 1000;
	const days = Math.floor((end.getTime() - start.getTime()) / dayMs) + 1;
	return Array.from({ length: Math.max(days, 1) }, (_, index) => addDays(start, index));
}

function generateWeekDates(startDate) {
	return generateDateRange(startDate, addDays(startDate, 6));
}

function formatISODate(date) {
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) {
		return '';
	}
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

function formatDisplay(date, includeYear = false) {
	const options = includeYear
		? { month: 'short', day: 'numeric', year: 'numeric' }
		: { month: 'short', day: 'numeric' };
	return date.toLocaleDateString(undefined, options);
}

const resolveInvoiceFileUrl = async (invoice) => {
	const file = invoice?.file;
	if (!file) return null;
	if (file.id) {
		const signed = await mediaStore.getSignedUrl(file.id);
		return signed?.url || file.url || null;
	}
	return file.url || null;
};

const deriveInvoiceFileName = (invoice) =>
	invoice?.attachmentName ||
	invoice?.file?.name ||
	(invoice?.invoiceNumber ? `supplier-invoice-${invoice.invoiceNumber}.pdf` : 'supplier-invoice.pdf');

const formatInvoiceChooserDate = (value) => {
	if (!value) return 'No invoice date';
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return 'No invoice date';
	return parsed.toLocaleDateString('en-GB');
};

const formatInvoiceChooserAmount = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return String(value);
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
	}).format(numeric);
};

const formatInvoiceChooserPeriod = (invoice) => {
	const parts = [invoice?.servicePeriodStart, invoice?.servicePeriodEnd]
		.map((value) => formatInvoiceChooserDate(value))
		.filter((value) => value !== 'No invoice date');
	if (!parts.length) return 'No period';
	if (parts.length === 1) return parts[0];
	return `${parts[0]} — ${parts[1]}`;
};

const downloadFileUrl = async (url, filename) => {
	if (!url) return;
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Download failed with status ${response.status}`);
		}
		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = filename || 'supplier-invoice.pdf';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(objectUrl);
	} catch (error) {
		logError?.(error, 'AppointmentsWeekView downloadFileUrl');
		toast?.error?.('Failed to download invoice.');
	}
};

const closeInvoicePreview = () => {
	showInvoicePreview.value = false;
	invoicePreviewPdfUrl.value = '';
	invoicePreviewFileName.value = '';
};

const closeInvoiceChooser = () => {
	showInvoiceChooser.value = false;
};

const selectInvoiceCandidate = (invoice) => {
	selectedInvoiceCandidateKey.value = getInvoiceSelectionKey(invoice);
	showInvoiceChooser.value = false;
};

const downloadInvoicePreviewPdf = async () => {
	if (!invoicePreviewPdfUrl.value) return;
	await downloadFileUrl(invoicePreviewPdfUrl.value, invoicePreviewFileName.value);
};

const handleShowInvoice = async () => {
	if (requiresInvoiceChoice.value) {
		showInvoiceChooser.value = true;
		return;
	}
	const match = matchedSupplierInvoice.value;
	if (!match || isInvoicePreviewOpening.value) return;
	isInvoicePreviewOpening.value = true;
	try {
		const url = await resolveInvoiceFileUrl(match);
		if (!url) {
			toast?.info?.('No invoice file attached.');
			return;
		}
		invoicePreviewPdfUrl.value = url;
		invoicePreviewFileName.value = deriveInvoiceFileName(match);
		showInvoicePreview.value = true;
	} catch (error) {
		logError?.(error, 'AppointmentsWeekView handleShowInvoice');
		toast?.error?.('Failed to open invoice preview.');
	} finally {
		isInvoicePreviewOpening.value = false;
	}
};

defineExpose({ applyTemplateToWeek, changeWeek, handleDatePickerUpdate, activeDateFrom, activeDateTo, isLoading });
onDeactivated(() => {
	openSubmenuId.value = null;
	editingFilterType.value = null;
	openDaySubmenuKey.value = null;
});

// What was entered on another device shows up here without reloading. The week on screen is
// read again quietly, and only replaced while nothing is being edited, saved or added.
const isWeekBusy = () =>
	isLoading.value ||
	isApplyingTemplate.value ||
	Boolean(timeDialog.value) ||
	Boolean(openSubmenuId.value || openDaySubmenuKey.value || editingFilterType.value) ||
	Object.keys(savingState).length > 0 ||
	Object.values(saveTimers).some(Boolean) ||
	Object.values(queuedSaves).some(Boolean) ||
	Object.values(addingState.value).some(Boolean) ||
	Object.values(deletingState.value).some(Boolean) ||
	(store.items || []).some((item) => item?.__pending);

const refreshWeekQuietly = async ({ canApply }) => {
	if (!currentJournalId.value || !hasLoadedWeek.value || isWeekBusy()) return;
	const { startDate, endDate } = activeRange.value;
	const filtersSnapshot = currentFilters();
	const cacheKey = getCacheKeyForRange(startDate, endDate, filtersSnapshot);
	if (!cacheKey) return;
	const fresh = await store.queryAppointmentsInRange({
		startDate: formatISODate(startDate),
		endDate: formatISODate(endDate),
		journalId: currentJournalId.value,
		filters: filtersSnapshot,
	});
	// The week or the filters changed while the request was out, or editing began.
	const now = activeRange.value;
	if (getCacheKeyForRange(now.startDate, now.endDate, currentFilters()) !== cacheKey) return;
	if (!canApply() || isWeekBusy()) return;
	// Other weeks may have changed too; drop them so they are read again when visited.
	weekCache.clear();
	cacheWeekItems(cacheKey, fresh);
	prefetchAdjacentWeeks(weekStartDate.value, filtersSnapshot);
	if (sameRecords(store.items, fresh)) return;
	const known = new Set((store.items || []).map((item) => rowKey(item)));
	setWeekItems(fresh);
	fresh.forEach((item) => {
		const key = rowKey(item);
		if (!known.has(key)) markRowAsNew(key);
	});
};

useLiveRefresh(refreshWeekQuietly, { isBusy: isWeekBusy });

// The office corrects when a finished job started and stopped; the week is then read again.
const openTimeDialog = (job) => {
	timeDialog.value = { job, saving: false, error: '' };
};

const saveTimes = async ({ startAt, endAt }) => {
	const dialog = timeDialog.value;
	if (!dialog) return;
	dialog.saving = true;
	dialog.error = '';
	try {
		await store.setTrackedTime({ id: dialog.job.id, startAt, endAt });
		timeDialog.value = null;
		await refreshWeekQuietly({ canApply: () => true });
	} catch (err) {
		dialog.saving = false;
		dialog.error = getErrorMessage(err, 'Could not save the times.');
	}
};
</script>

<style scoped>
.noc-back-btn:not(.secondary):hover:not(:disabled) {
	background-color: var(--primary-hover, #0855b3);
	filter: none;
}

button,
.noc-back-btn {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	font: var(--ap-type-control);
}

.appointments-week {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}

/* The header lays out the filters, the week navigation and the view switch on one grid.
   Wide screens: filters and navigation side by side, the switch below.
   Tablet landscape: filters across the top, the navigation beside the switch.
   Tablet portrait: one row each. The navigation never wraps inside itself. */
.journal-toolbar {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	grid-template-areas:
		'filters nav'
		'views views';
	align-items: center;
	gap: 16px 12px;
}

.journal-toolbar > .filters-bar {
	grid-area: filters;
}

.journal-toolbar-actions {
	grid-area: nav;
	justify-self: end;
	max-width: 100%;
}

/* View switcher */
.journal-tabs-row {
	grid-area: views;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
	min-width: 0;
}

/* Breakpoints assume the 220px sidebar; touch screens fold it into a 12px strip (208px more room). */
@media (max-width: 1279px) and (hover: hover), (max-width: 1279px) and (pointer: fine), (max-width: 1071px) {
	.journal-toolbar {
		grid-template-areas:
			'filters filters'
			'views nav';
	}
}

@media (max-width: 1107px) and (hover: hover), (max-width: 1107px) and (pointer: fine), (max-width: 899px) {
	.journal-toolbar {
		grid-template-columns: minmax(0, 1fr);
		grid-template-areas:
			'filters'
			'views'
			'nav';
	}

	.journal-toolbar-actions {
		justify-self: start;
	}
}

.journal-tabs-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

/* Compact filter bar */
.filters-bar {
	display: flex;
	min-width: 0;
	align-items: center;
	gap: 8px;
	padding: 2px 12px;
	flex-wrap: wrap;
	position: relative;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: none;
	min-height: 36px;
	max-width: 100%;
	box-sizing: border-box;
}

.filters-bar-start {
	display: flex;
	align-items: center;
	color: var(--text-color-secondary, #6b7280);
}


.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	height: 30px;
	padding: 0 4px 0 10px;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 6px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #374151);
	white-space: nowrap;
	box-shadow: none;
	font: var(--ap-type-control);
}

.filter-chip-text {
	line-height: inherit;
}

.filter-chip-remove {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	padding: 4px;
	border-radius: 4px;
	font-size: 0.8rem;
}

.filter-chip-remove:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}

.filter-label-btn {
	display: inline-flex;
	align-items: center;
	height: 30px;
	padding: 0 10px;
	border: none;
	background: none;
	color: var(--text-color-secondary, #6b7280);
	cursor: pointer;
	border-radius: 6px;
	white-space: nowrap;
	font: var(--ap-type-control);
}

.filter-label-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}

.filter-inline-editor {
	position: relative;
	width: 100%;
	min-width: 0;
}

.filter-editor-search {
	width: 100%;
}

.filter-editor-search :deep(.field-input) {
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 6px;
	padding: 6px 10px;
	height: 30px;
	font: var(--ap-type-body);
}

.filter-editor-search :deep(.field-input):focus {
	border-color: var(--noc-blue, #0065ff);
}

.filter-inline-text-input {
	width: 100%;
	box-sizing: border-box;
	height: 30px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 6px;
	padding: 4px 10px;
	min-width: 0;
	outline: none;
	color: var(--text-color, #374151);
	background: var(--noc-background, #fff);
	transition: border-color 0.15s;
	font: var(--ap-type-body);
}

.filter-inline-text-input:focus {
	border-color: var(--noc-blue, #0065ff);
}

.inline-error {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	border: 1px solid var(--noc-red, #c45151);
	border-radius: 6px;
	padding: 12px 16px;
	background-color: var(--noc-red-05, rgba(196, 81, 81, 0.05));
	color: var(--noc-red, #c45151);
}

.inline-error p {
	margin: 0;
	flex: 1;
	font: var(--ap-type-body);
}

.week-content-shell {
	position: relative;
}

.week-rows {
	display: flex;
	flex-direction: column;
	gap: 0;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: none;
	padding: 0;
	background: var(--border-color, #dcdcdc);
	overflow: hidden;
}

.week-initial-loading {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 1;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 16px;
	color: var(--text-color-secondary, #5e6170);
	font: var(--ap-type-label);
	/* Stays visible while the week behind it is still hidden. */
	visibility: visible;
	/* A quick first load never shows the loader; a slow one gets it quietly. */
	animation: week-loader-enter 320ms ease 450ms backwards;
}

/* Gone before the first day starts to appear (130ms), so their labels never overlap. */
.week-loader-leave-active {
	transition: opacity 120ms ease;
}

.week-loader-leave-to {
	opacity: 0;
}

@keyframes week-loader-enter {
	from {
		opacity: 0;
	}
}

/* First load: the toolbar and the week wait for the appointments, then settle in from the top. */
.appointments-week:is(.is-waiting, .is-settling) :is(.journal-toolbar, .week-rows, .state-error) {
	visibility: hidden;
}

.appointments-week.is-revealing :is(.journal-toolbar > *, .day-row, .state-error) {
	animation: week-reveal 640ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.appointments-week.is-revealing .journal-tabs-row {
	animation-delay: 60ms;
}

.appointments-week.is-revealing :is(.day-row, .state-error) {
	animation-delay: calc(130ms + var(--row-index, 0) * 45ms);
}

@keyframes week-reveal {
	from {
		opacity: 0;
		transform: translateY(12px);
	}
}

@keyframes week-reveal-fade {
	from {
		opacity: 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.week-initial-loading {
		animation: none;
	}

	.appointments-week.is-revealing :is(.journal-toolbar > *, .day-row, .state-error) {
		animation-name: week-reveal-fade;
		animation-delay: 0ms;
	}
}

.day-row {
	background: var(--noc-background, #fff);
	padding: 16px 0;
	display: flex;
	flex-direction: column;
	gap: 0;
}

.week-rows .day-row:first-child {
	padding-top: 0;
}

.week-rows .day-row:not(:last-child) {
	margin-bottom: 0;
}

.week-rows.journal-days,
.week-rows.summary-mode {
	gap: 12px;
	border: none;
	border-radius: 0;
	background: transparent;
	overflow: visible;
}

.journal-days > .day-row,
.summary-mode > .summary-day {
	padding: 0;
	background: transparent;
}

.summary-mode > .summary-day {
	gap: 12px;
}

.day-card,
.summary-section {
	border-radius: 0;
}

.day-card > .day-header,
.summary-section > .day-header {
	background: transparent;
	border-radius: 0;
}

.day-header {
	display: flex;
	flex-direction: row;
	gap: 12px;
	align-items: center;
	padding: 0 12px 0 16px;
	justify-content: space-between;
	height: 40px;
	min-height: 40px;
}

.day-actions {
	display: flex;
	align-items: center;
	margin-left: auto;
}

.day-footer {
	display: flex;
	padding: 0 16px 12px;
	justify-content: flex-start;
	margin-top: 8px;
}

.day-footer .noc-back-btn.secondary {
	border-radius: 8px;
	box-shadow: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	border: 1px solid var(--border-color, #e5e7eb);
	height: 34px;
	padding: 0 13px;
	font: var(--ap-type-control);
}

.day-footer .noc-back-btn.secondary:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	filter: none;
}

.day-label {
	text-align: left;
	font: var(--ap-type-section);
}

.appointments-container {
	position: relative;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 0;
	border-top: 1px solid var(--border-color, #dcdcdc);
	border-bottom: 1px solid var(--border-color, #dcdcdc);
	border-inline: 1px solid var(--border-color, #dcdcdc);
}

/* Keep blur on a separate layer so fixed cell menus stay anchored to the viewport. */
.appointments-container::before {
	content: '';
	position: absolute;
	inset: 0;
	/* Cover the full scrollable grid on narrow screens (the rows' minimum width). */
	min-width: 780px;
	border-radius: 0;
	background: rgb(255 255 255 / 94%);
	pointer-events: none;
}

.completed-summary-table::before {
	min-width: 1040px;
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
	.appointments-container::before {
		background: linear-gradient(135deg, rgb(255 255 255 / 72%), rgb(255 255 255 / 48%));
		-webkit-backdrop-filter: blur(16px) saturate(115%);
		backdrop-filter: blur(16px) saturate(115%);
	}
}

@media (prefers-reduced-transparency: reduce) {
	.appointments-container::before {
		background: #fff;
		-webkit-backdrop-filter: none;
		backdrop-filter: none;
	}
}

.appointment-row {
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	padding: 0;
	background-color: transparent;
	gap: 0;
	border: none;
	border-radius: 0;
}

.appointment-row:not(:last-child) {
	border-bottom: 1px solid var(--border-color, #dcdcdc);
}

/* Align horizontal separators with the first column's text without moving cells. */
.journal-days .appointment-row:not(:last-child),
.summary-table .appointment-row:not(:last-child) {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color, #dcdcdc) 16px) 1;
}

.appointment-row.is-pending {
	pointer-events: none;
}

.appointment-details {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0;
}

.dynamic-fields {
	width: 100%;
	display: grid;
	grid-template-columns: repeat(3, minmax(140px, 2fr)) repeat(3, minmax(100px, 1fr));
	gap: 0;
	align-items: stretch;
}

.field-input-container {
	display: flex;
	min-width: 0;
}

.field-input-container:not(:first-child) {
	border-left: 1px solid var(--border-color, #dcdcdc);
}

.field-input-container :deep(.catalog-search-input) {
	width: 100%;
}

.field-input-container :deep(.catalog-search-input .field-input),
.field-input {
	min-height: 36px;
	padding: 8px 16px;
	border: none;
	border-radius: 0;
	background-color: transparent;
	color: var(--text-color, #000);
	outline: none;
	transition: background-color 0.1s ease;
	width: 100%;
	font: var(--ap-type-body);
}

.field-input-container.field-status .field-select {
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
	background-image: none;
	padding-right: 16px;
	text-align: left;
	text-align-last: left;
	cursor: pointer;
}

.field-input-container :deep(.catalog-search-input .field-input):hover,
.field-input:hover {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 3%, transparent);
	cursor: text;
}

.field-select option {
	background: var(--noc-background, #fff);
}

.appointment-row .field-input-container :deep(.catalog-search-input .field-input:focus),
.appointment-row .field-input:focus {
	border: none !important;
	box-shadow: none;
}

.summary-label {
	display: flex;
	align-items: center;
	gap: 8px;
	color: var(--text-color);
	font: var(--ap-type-section);
}

.summary-input {
	display: block;
	box-sizing: border-box;
	min-height: 40px;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
}

.summary-input,
.summary-input:hover {
	color: var(--text-color, #111);
	background: transparent;
	cursor: default;
	opacity: 1;
}

.summary-section {
	display: flex;
	flex-direction: column;
	background: transparent;
	padding-bottom: 12px;
	overflow: hidden;
}

.summary-section:first-child,
.summary-section-with-invoice {
	padding-bottom: 0;
}

.summary-mode .state {
	padding: 24px 16px;
	background: var(--noc-background, #fff);
	border-radius: 8px;
}

.summary-section > .state {
	padding-bottom: 12px;
}

.summary-table {
	overflow-x: auto;
}

.summary-table > * {
	flex-shrink: 0;
	min-width: 580px;
}

.completed-summary-table > * {
	min-width: 1040px;
}

.summary-fields {
	grid-template-columns: minmax(100px, 6fr) minmax(100px, 6fr) repeat(3, minmax(100px, 5fr));
}

.summary-fields.summary-fields-no-supplier {
	grid-template-columns: minmax(280px, 12fr) repeat(3, minmax(100px, 5fr));
}

.summary-header-row {
	position: relative;
	display: flex;
	width: 100%;
	background-color: rgb(250 250 250 / 35%);
	border-bottom: 1px solid var(--border-color, #dcdcdc);
}

.summary-header-row .dynamic-fields {
	width: 100%;
}

.field-header {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	color: var(--text-color, #414552);
	text-transform: none;
	font: var(--ap-type-table-label);
	font-weight: 600;
}

.summary-fields-service {
	grid-template-columns: minmax(280px, 12fr) repeat(3, minmax(100px, 5fr));
}

.summary-fields-completed {
	grid-template-columns:
		minmax(110px, 2.5fr)
		minmax(150px, 4fr)
		minmax(120px, 3fr)
		minmax(120px, 3fr)
		minmax(60px, 1.5fr)
		minmax(90px, 2fr)
		minmax(100px, 2fr)
		minmax(280px, 9fr);
}

.completed-summary-row {
	align-items: stretch;
}

.summary-history-cell-container {
	align-items: stretch;
}

.summary-multiline-cell {
	width: 100%;
	min-height: 40px;
	padding: 8px 16px;
	background: transparent;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.summary-multiline-item {
	color: var(--text-color, #111);
	word-break: break-word;
	font: var(--ap-type-caption);
}

.summary-location-link {
	color: var(--noc-blue, #0b5ae0);
	text-decoration: none;
}

.summary-location-separator {
	margin: 0 4px;
	color: var(--text-secondary-color, #6b7280);
}

/* A start or stop away from the site stands out; a missing location reads as quieter. */
.summary-multiline-item.is-away {
	color: var(--noc-red, #d32f2f);
}

.summary-multiline-item.is-missing {
	color: var(--text-secondary-color, #6b7280);
}

/* The pencil beside a job's start and stop opens the editor. */
.summary-edit-times {
	display: inline-flex;
	flex: none;
	align-self: flex-start;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	margin: 6px 8px 0 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary, #5e6170);
	cursor: pointer;
}

.summary-edit-times:hover {
	background: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	color: var(--noc-blue, #0a61c9);
}

.summary-edit-times:focus-visible {
	outline: 1px solid var(--noc-blue, #0a61c9);
	outline-offset: -1px;
}

.summary-location-link:hover {
	text-decoration: underline;
}

.summary-totals-container {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	width: 100%;
	margin-top: 8px;
}

.summary-totals-spacer {
	flex: 17 1 0;
	min-width: 380px;
	display: flex;
	align-items: center;
}

.summary-totals-spacer .noc-back-btn,
.summary-invoice-action {
	height: 36px;
	padding-left: 22px;
	padding-right: 22px;
	background: var(--noc-blue);
	color: #fff;
}

.summary-totals-spacer .noc-back-btn {
	margin-left: 16px;
}

.summary-totals-content {
	flex: 10 1 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.summary-totals-row {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	width: 100%;
}

.summary-totals-cell {
	padding: 8px 16px;
	min-height: 36px;
	display: flex;
	align-items: center;
}

.summary-totals-amount {
	color: var(--text-color, #111);
	font: var(--ap-type-control);
}

.summary-totals-row.summary-totals-final {
	background-color: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	border: none;
	border-radius: 6px;
}

.summary-totals-row.summary-totals-final .summary-totals-amount {
	color: var(--text-color, #414552);
	font: var(--ap-type-total);
}


.appointment-row .field-input-container :deep(.catalog-search-input .field-input)::placeholder,
.appointment-row .field-input::placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.appointment-row .field-input-container :deep(.catalog-search-input .field-input:hover),
.appointment-row .field-input:hover {
	border: none !important;
	box-shadow: none;
}
.appointment-row .field-input-container :deep(.catalog-search-input .field-input:focus),
.appointment-row .field-input:focus {
	border: none !important;
	box-shadow: none;
}

.field-number {
	text-align: left;
}

.field-number,
.summary-input,
.summary-totals-row .summary-totals-amount,
.summary-totals-row.summary-totals-final .summary-totals-amount {
	font-variant-numeric: tabular-nums;
}

/* Stays at the right edge while a narrow screen scrolls the table sideways. */
.appointment-actions {
	display: flex;
	gap: 0;
	align-items: center;
	padding-right: 12px;
	position: sticky;
	right: 0;
	z-index: 2;
}

.appointments-week .appointment-actions {
	background: rgb(255 255 255 / 94%);
}

/* Every row's action column is sticky with its own layer; lift the one whose menu is open above the rest. */
.appointment-row.submenu-open .appointment-actions {
	z-index: 30;
}

/* The sticky ⋯ column needs a backing only while the table scrolls sideways (rows are 780px at least).
   With the touch strip instead of the sidebar, the table fits from 840px. */
@media (min-width: 1048px), (min-width: 840px) and (hover: none) and (pointer: coarse) {
	.appointments-week .appointment-actions {
		background: transparent;
	}
}

.action-wrapper {
	position: relative;
}

.more-options-btn {
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	font-size: 1.1rem;
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	line-height: 1;
}

.more-options-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}


/* Row menus open with the same short fade-and-slide as the filter menus. */
.appointment-row :deep(.dropdown-menu),
.submenu {
	animation: row-menu-enter 160ms ease-out both;
}

.appointment-row :deep(.dropdown-menu.is-above) {
	--row-menu-enter-offset: 4px;
}

@keyframes row-menu-enter {
	from {
		opacity: 0;
		transform: translateY(var(--row-menu-enter-offset, -4px));
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.appointment-row :deep(.dropdown-menu),
	.submenu {
		animation: none;
	}
}

.submenu {
	position: absolute;
	right: 0;
	top: 100%;
	background-color: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	z-index: 10;
	min-width: 150px;
	padding: 4px 0;
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 8px 16px;
	background: none;
	border: none;
	color: var(--text-color, #000);
	text-align: left;
	cursor: pointer;
	white-space: nowrap;
	font: var(--ap-type-control);
}

.submenu-btn.delete {
	color: var(--noc-red, #c45151);
}

.submenu-btn.delete:hover:not(:disabled) {
	background-color: var(--noc-red-10, rgba(196, 81, 81, 0.1));
	color: var(--noc-red, #c45151);
}

.submenu-btn:not(.delete):hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}

.submenu-btn:disabled {
	opacity: 1;
}

.add-btn {
	align-self: center;
	margin: 0;
}

.primary-btn.add-btn {
	border-radius: 4px;
	padding: 8px 16px;
	height: 34px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	box-shadow: var(--box-shadow);
	font: var(--ap-type-control);
}


.primary-btn,
.outline-btn,
.text-btn,
select,
input {
	font: var(--ap-type-body);
}

.primary-btn {
	background: var(--noc-blue, #0065ff);
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 20px;
	cursor: pointer;
}

.primary-btn:hover:not(:disabled) {
	background-color: var(--primary-hover, #0855b3);
}

.primary-btn:disabled {
	opacity: 1;
}

.outline-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.outline-btn {
	border: 1px solid var(--border-color, #ccc);
	background: transparent;
	border-radius: 4px;
	padding: 8px 20px;
	cursor: pointer;
}

.appointments-week :deep(.preview-btn) {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.appointments-week :deep(.preview-btn:hover:not(:disabled)) {
	background-color: var(--noc-blue-10, rgba(10, 97, 201, 0.1));
	color: var(--noc-blue, #0a61c9);
	opacity: 1;
}

.text-btn {
	border: none;
	background: none;
	color: var(--noc-red, #c45151);
	padding: 0;
	cursor: pointer;
	font: var(--ap-type-control);
}

.state {
	padding: 24px 0 8px;
	text-align: center;
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
}

.state-error {
	color: var(--noc-red, #c45151);
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
}

.week-loading-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.82);
	z-index: 20;
	pointer-events: auto;
	border-radius: 8px;
}

.week-loading-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	border-radius: 999px;
	border: 1px solid var(--border-color, #dcdcdc);
	background: rgba(255, 255, 255, 0.96);
	color: var(--text-color-secondary, #4b5563);

	font: var(--ap-type-label);
}

.week-loading-spinner {
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid #d3d7df;
	border-top-color: var(--noc-blue, #0065ff);
	animation: week-loading-spin 0.6s linear infinite;
}

.invoice-chooser-overlay {
	position: fixed;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(17, 24, 39, 0.35);
	z-index: 40;
}

.invoice-chooser-modal {
	width: min(560px, 100%);
	max-height: min(80vh, 720px);
	display: flex;
	flex-direction: column;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #d9d9d9);
	border-radius: 16px;
	box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
	overflow: hidden;
}

.invoice-chooser-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding: 20px 24px 16px;
	border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.invoice-chooser-header h3 {
	margin: 0;
	font: var(--ap-type-modal-title);
}

.invoice-chooser-header p {
	margin: 6px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font: var(--ap-type-caption);
}

.invoice-chooser-close:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.invoice-chooser-close {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 999px;
	background: transparent;
	cursor: pointer;
	color: var(--text-color-secondary, #4b5563);
}

.invoice-chooser-list {
	display: flex;
	flex-direction: column;
	padding: 12px;
	overflow: auto;
}

.invoice-chooser-item {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
	padding: 14px 16px;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 12px;
	background: transparent;
	cursor: pointer;
	text-align: left;
}

.invoice-chooser-item + .invoice-chooser-item {
	margin-top: 10px;
}

.invoice-chooser-item:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
	border-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 20%, transparent);
}

.invoice-chooser-line {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	color: var(--text-color, #1f2937);
	font: var(--ap-type-body);
}

@keyframes week-loading-spin {
	to {
		transform: rotate(360deg);
	}
}
.primary-btn,
.outline-btn,
.text-btn {
	font: var(--ap-type-control);
}

.catalog-search-input :deep(.dropdown-menu li),
.catalog-search-input :deep(.dropdown-menu.no-results) {
	font: var(--ap-type-body);
}

.catalog-search-input :deep(.dropdown-menu li.is-selected) {
	font-weight: 500;
}
.invoice-chooser-line strong {
	font-weight: 500;
}
</style>

<style scoped>
.pay-summary-error { display: block; color: #a4332a; font-size: 11px; white-space: normal; }
</style>
